import {
  CPSMessages,
  PageNames,
  AxiosErrorCode,
  HttpStatusCode,
} from "commonUtils/enumerations";
import {
  CountriesProps,
  DropdownList,
  DropdownWithStatusType,
  NameProps,
  ParticipantRoleType,
  SpecialtiesProps,
  StatesProps,
} from "commonUtils/genericInterfaces";
import {scrollToTop} from "commonUtils/utils";
import {
  ServiceAuthenticationException,
  HttpException,
} from "services/exceptionHandlerService";

export interface CalendarCountsProps {
  Subject: string;
  StartTime: string;
  EndTime: string;
  activityCount: number;
}

export interface CalendarActivitiesProps {
  activityCalendarActivities: [
    {
      activityIdentity: string;
      activityURL: string;
      activityInfoSheetURL: string;
      activityDescription: string;
      startDateTime: string;
      addressCityState: string;
      brand: string;
    },
  ];
  totalRecordsCount: number;
  messageCode: number;
  message: string | null;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
}

export const handleAxiosErrors = (
  error: any,
  setErrorMsg: (arg0: CPSMessages) => void,
  setShowAlert?: any,
  setServerValidation?: any,
  isScrollToTop?: boolean,
) => {
  const data = {pagename: PageNames.ACTIVITY};
  if (
    error?.code === AxiosErrorCode.ERR_BAD_REQUEST ||
    error?.code === AxiosErrorCode.ERR_BAD_RESPONSE
  ) {
    if (
      error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
      error?.response?.status === HttpStatusCode.FORBIDDEN
    ) {
      ServiceAuthenticationException(error, data, error?.response?.status);
    } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
      setErrorMsg(error?.response?.data?.message);
      if (isScrollToTop) {
        scrollToTop(0, "smooth");
      }
      setShowAlert && setShowAlert(true);
      setServerValidation && setServerValidation(error?.response?.data);
    } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
      setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      if (isScrollToTop) {
        scrollToTop(0, "smooth");
      }
      setShowAlert && setShowAlert(true);
      setServerValidation &&
        setServerValidation({
          message: CPSMessages.SERVICE_NOT_FOUND,
          messageCode: 0,
          isSuccessMessage: false,
          isValidationMessage: false,
          isErrorMessage: true,
        });
    } else if (
      error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
    ) {
      HttpException(error, data);
      setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      if (isScrollToTop) {
        scrollToTop(0, "smooth");
      }
      setShowAlert && setShowAlert(true);
      setServerValidation &&
        setServerValidation({
          message: CPSMessages.INTERNAL_SERVER_ERROR,
          messageCode: 0,
          isSuccessMessage: false,
          isValidationMessage: false,
          isErrorMessage: true,
        });
    } else {
      setErrorMsg(error?.message);
    }
  } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
    setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    if (isScrollToTop) {
      scrollToTop(0, "smooth");
    }
    setShowAlert && setShowAlert(true);
    setServerValidation &&
      setServerValidation({
        message: CPSMessages.TIMEOUT_ERROR,
        messageCode: 0,
        isSuccessMessage: false,
        isValidationMessage: false,
        isErrorMessage: true,
      });
  } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
    setErrorMsg(CPSMessages.NETWORK_ERROR);
    if (isScrollToTop) {
      scrollToTop(0, "smooth");
    }
    setShowAlert && setShowAlert(true);
    setServerValidation &&
      setServerValidation({
        message: CPSMessages.NETWORK_ERROR,
        messageCode: 0,
        isSuccessMessage: false,
        isValidationMessage: false,
        isErrorMessage: true,
      });
  } else {
    setErrorMsg(error?.message);
  }
};

export interface ExpenseCorrespondenceEmailTemplates {
  id: number;
  name: string;
  alertTypeId: number;
}
export interface ExpenseCorrespondenceFormValue {
  emailTemplate: number;
  activityIdentity: string;
  firstName: string;
  lastName: string;
}
export interface ExpenseCorrespondenceRow {
  activityId: number;
  activityIdentity: string;
  facultyId: number;
  facultyName: string;
}
export interface ActivityDescriptions {
  id: number;
  name: string;
  activityTypeId: number;
  activitySubTypeId: number;
  status: boolean;
  ruleActivityTypeId: number;
  isTraining: boolean;
  brandIds: string;
  isARFActivityDescription: boolean;
  faTypeId: number;
  meetingTypeId: number;
  mappedCostElement: string;
  attendeeTypeIds: string;
}
export interface ARFBasicSearchFormValues {
  arfId: number;
  tlMaPCAId: string;
  startDate: string | null;
  endDate: string | null;
  activityDescriptionId: number;
  cmsAssociate: string;
  advisingCentralContract: string;
}
export interface ARFSearchActivitiesRow {
  activityId: number;
  rowId: number;
  isActivityIdentityHyperlinked: boolean;
  activityIdentity: string;
  activityTypeId: number;
  loadActivityURL: string;
  isInfoSheetIconVisible: boolean;
  arfId: number;
  isARFNumberVisible: boolean;
  isARFNumberHyperlinked: boolean;
  arfNumber: string;
  loadARFURL: string;
  arfStatus: string;
  tlMaPCAId: string;
  startDate: string;
  endDate: string;
  cmsAssociate: string;
  advisingCentralContract: string;
  isPaymentRequestHyperlinkVisible: boolean;
  isCheckStatusHyperlinkVisible: boolean;
  isCreatePNRHyperlinkVisible: boolean;
  isPNRNumberVisible: boolean;
  pnrNumber: string;
  isContracted: boolean;
  isSingleFaculty: boolean;
  isMultipleActivities: boolean;
  isFromAHMFeed: boolean;
  isEditProfileHyperlinkVisible: boolean;
  travelOwnerName: string;
  isActivityCommentHyperlinkVisible: boolean;
  loadActivityCommentsURL: string;
  isAddCommentHyperlinkVisible: boolean;
  isReadOnlyMode: boolean;
  isCMSAssociateEnabled: boolean;
  facultyId: number;
  activityType: string;
  isDraftActivity: boolean;
  isFeedBack: number;
}
export interface AdvanceSearchBrands {
  id: number;
  brandName: string;
  status: boolean;
  supplierId: number;
}
export interface ARFAdvanceSearchCountry {
  id: number;
  name: string;
}
export interface AdvanceSearchState {
  id: number;
  name: string;
  countryId: number;
  shortName: string;
}

export interface PaginationAndSort {
  pageIndex: number;
  pageSize: number;
  sortCol: string;
  sortOrder: string;
}

export interface ActivityBasicSearchForm {
  activityNumber: string;
  pidNumber: string;
  brandId: number;
  startDate: string;
  endDate: string;
  activityDescriptionId: number;
  stateId: number;
  supplierName: string;
}

export interface ActivityAdvanceSearchForm {
  activityNumber: string;
  pidNumber: string;
  supplierName: string;
  startDate: string;
  endDate: string;
  facultyLastName: string;
  facultyFirstName: string;
  facultyId: number;
  lillyPersonId: string;
  brandId: number;
  representativeLastName: string;
  representativeFirstName: string;
  city: string;
  stateId: number;
  countryId: number;
}

export interface ActivitiesSearchRow {
  rowId: number;
  activityId: number;
  isActivityIdentityHyperlinked: boolean;
  activityIdentity: string;
  activityTypeId: number;
  loadActivityURL: string;
  isInfoSheetIconVisible: boolean;
  supplierShortName: string;
  startDateTime: string;
  endDateTime: string;
  activityLocation: string;
  cityState: string;
  brand: string;
  isFacultyIdHyperlinked: boolean;
  facultyId: number;
  isLillyPersonIdHyperlinked: boolean;
  lillyPersonId: string;
  isFacultyNameHyperlinked: boolean;
  facultyName: string;
  cpsStatus: string;
  isSBStatusVisible: boolean;
  sbStatus: string;
  isPaymentRequestHyperlinkVisible: boolean;
  isCheckStatusHyperlinkVisible: boolean;
  isCreatePNRHyperlinkVisible: boolean;
  isPNRNumberVisible: boolean;
  pnrNumber: string;
  isContracted: boolean;
  isSingleFaculty: boolean;
  isMultipleActivities: boolean;
  isFromAHMFeed: boolean;
  isTravelParameterHyperlinkVisible: boolean;
  travelParameter: string;
  travelType: string;
  isEditProfileHyperlinkVisible: boolean;
  travelOwnerName: string;
  isActivityCommentHyperlinkVisible: boolean;
  loadActivityCommentsURL: string;
  isAddCommentHyperlinkVisible: boolean;
  activityType: string;
  isDraftActivity: boolean;
  isFeedBack: number;
  isLinkPNRVisible: boolean;
}

export interface SearchDataGridConfiguration {
  isSupplierShortNameColumnVisible: boolean;
  isActivityLocationColumnVisible: boolean;
  isBrandColumnVisible: boolean;
  isActivityStatusColumnVisible: boolean;
  cpsStatusHeaderText: string;
  isCPSStatusInformationIconVisible: boolean;
  isSBStatusHeaderTextVisible: boolean;
  isSBStatusInformationIconVisible: boolean;
  isLTSSupplier: boolean;
}

export interface FacultyType {
  facultyId: number;
  lillyPersonId: string;
  facultyFullName: string;
  caregiverFullName: string | null;
  isFacultyNotContracted: boolean;
  facultyType: string;
  meetingDescription: string | null;
  activityMeetingStartDate: string;
  activityMeetingEndDate: string;
  activityMeetingStartTime: string;
  activityMeetingEndTime: string;
  location: string | null;
  travelType: string;
  isCreatePNRHyperlinkVisible: boolean;
  isPNRNumberVisible: boolean;
  isPNRNumberHyperlinked: boolean;
  pnrNumber: string;
  viewItineraryTitle: string | null;
  isContracted: boolean;
  isSingleFaculty: boolean;
  isMultipleActivities: boolean;
  isEditProfileHyperlinkVisible: boolean;
  isTravelParameterHyperlinkVisible: boolean;
  isTravelParameterHyperlinkEnabled: boolean;
  travelParameter: string;
  firstName: string;
  lastName: string;
  activityMeetingInformationId: number;
  rowId?: number;
  isLinkPNRVisible: boolean;
  activityType: string;
  isFromAHMFeed: boolean;
  isFeedBack: number;
}

export interface SponsorInformation {
  requestor: string;
  medicalBusinessOwner: string;
  scientificOwner: string;
  invoiceApprover: string;
}
export interface CompoundInformation {
  trialAlias: string;
  compoundName: string;
  lyCompound: string;
  therapeuticArea: string;
}
export interface Consultants {
  consultantName: string;
  status: string;
  facultyId: number;
  consultantType: string;
  contractStartDate: string;
  contractEndDate: string;
  budgetCategory: string;
  feeForService: string;
  expense: string;
  totalBudget: string;
}

export interface MeetingInformation {
  activityMeetingInformationId: number;
  meetingDescription: string;
  consultants: string;
  travelType: string;
  startDateTime: string;
  location: string;
  accommodation: string;
  meetingStatus: string;
  gtmsId: string;
  mpcEmails: string;
  mpcSupplierName: string;
}
export interface TravelParameters {
  activityMeetingInformationId: number;
  consultantName: string;
  travelParameter: string;
}

export interface MedicalActivityInfosheetDetails {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  supplierName: string;
  activityStatus: string;
  startDate: string;
  endDate: string;
  activityType: string;
  activityDescription: string;
  generalTitleOfService: string;
  sponsorInformation: SponsorInformation;
  compoundInformation: CompoundInformation[];
  consultantAndBudgetInformation: {
    consultants: Consultants[];
    totalPlannedFeeForService: string;
    totalPlannedExpense: string;
    totalPlannedBudget: string;
    totalActualFeeForService: string;
    totalActualExpense: string;
    totalActualBudget: string;
  };
  isTravelTypeColumnVisible: boolean;
  meetingInformation: MeetingInformation[];
  isTravelParameterSectionVisible: boolean;
  travelParameters: TravelParameters[];
  isRegisteredMeetingInformationGridVisible: boolean;
}

export interface ConsultantInformations {
  name: string;
  facultyId: number;
  consultantStatus: string;
  contractStartDate: string;
  contractEndDate: string;
  contractStatus: string;
}
export interface MeetingInformations {
  description: string;
  consultants: string;
  travelType: string;
  startDateTime: string;
  location: string;
}
export interface LillyMedicalContactInformations {
  requestor: string;
  email: string;
  costCenter: string;
  costCenterPercent: string;
  wbsElement: string;
}
export interface ActivityInfosheetLTSDetails {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  brand: string;
  startDateTime: string;
  endDateTime: string;
  lillyMedicalContactInformations: LillyMedicalContactInformations[];
  consultantInformations: ConsultantInformations[];
  isTravelTypeColumnVisible: boolean;
  meetingInformations: MeetingInformations[];
}

export interface BrandFaculties {
  facultyId: number;
  facultyName: string;
  cityState: string;
  facultyType: string;
  organizationalContactsLabel: string;
  organizationalContacts: string;
  organizationalContactsTooltip: string;
  specialties: string;
  travelType: string;
  contractStatus: string;
}
export interface BrandTravelParameters {
  facultyName: string;
  travelParameter: string;
}
export interface ActivityInfosheetBrandsDetails {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  supplier: string;
  activityIdentity: string;
  status: string;
  startDateTime: string;
  endDateTime: string;
  type: string;
  description: string;
  brandLabel: string;
  brand: string;
  topicLabel: string;
  topic: string;
  lillyAssociateOrPrimaryContact: string;
  contactPersonForActivityQuestions: string;
  locationName: string;
  address: string;
  isOrganizationalContactsColumnVisible: boolean;
  isTravelTypeColumnVisible: boolean;
  isContractStatusColumnVisible: boolean;
  faculties: BrandFaculties[];
  isTravelParameterSectionVisible: boolean;
  travelParameters: BrandTravelParameters[];
  meetingStatus: string | null;
  mpcSupplierName: string;
  mpcEmails: string;
  gtmsId: string;
}

export interface InfoSheetData {
  activityId: number;
  isFromAHMFeed: boolean;
}
export interface InfoSheetActivityIdData {
  activityId: number;
}

export interface FeedbackActivityInfosheetDetails {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  supplier: string;
  activityIdentity: string;
  status: string;
  approvalStatus: string;
  isApprovalStatusByWhenVisible: boolean;
  approvalStatusByWhenLabel: string;
  approvalStatusByWhen: string;
  startDateTime: string;
  endDateTime: string;
  type: string;
  description: string;
  businessAreaLabel: string;
  businessArea: string;
  diseaseStateLabel: string;
  diseaseState: string;
  therapeuticAreas: string;
  productLabel: string;
  product: string;
  tlMaPTopicAnticipatedWorkProductLabel: string;
  tlMaPTopicAnticipatedWorkProduct: string;
  commercialDecisionStatus: string;
  contractTopicLabel: string;
  contractTopic: string;
  lillyActivityOwner: string;
  isCMSContactForActivityQuestionsVisible: boolean;
  cmsContactForActivityQuestions: string;
  approverLevel1: string;
  approverLevel2: string;
  approverLevel3: string;
  isLillyMarketResearchDirectorVisible: boolean;
  lillyMarketResearchDirector: string;
  isAdvisingCentralMemberVisible: boolean;
  advisingCentralMember: string;
  isBudgetInformationSectionVisible: boolean;
  isBudgetApprovalVisible: boolean;
  budgetApprovalStatus: string;
  budgetByWhenLabel: string;
  budgetByWhen: string;
  plannedHonorarium: string;
  plannedFacultyExpenseReimbursement: string;
  plannedTotalFacultyPayments: string;
  plannedLogistics: string;
  plannedEducationalItems: string;
  plannedTotalActivityBudget: string;
  actualHonorarium: string;
  actualFacultyExpenseReimbursement: string;
  actualTotalFacultyPayments: string;
  actualEducationalItems: string;
  actualTotalActivityBudget: string;
  isEducationalItemSectionVisible: boolean;
  educationalItems: EducationalItems[];
  isLocationSectionVisible: boolean;
  locationName: string;
  address: string;
  isAccommodationInformationSectionVisible: boolean;
  isAccommodationInformationSame: boolean;
  accommodationName: string;
  accommodationAddress: string;
  isParticipantsInformationSectionVisible: boolean;
  isFacultyOver20ApprovalStatusVisible: boolean;
  facultyOver20ApprovalStatus: string;
  isFacultyOver20ByWhenVisible: boolean;
  facultyOver20ByWhenLabel: string;
  facultyOver20ByWhen: string;
  isTotalFacultyCountVisible: boolean;
  totalFacultyCount: number;
  isOrganizationalContactsColumnVisible: boolean;
  isTravelTypeColumnVisible: boolean;
  isContractStatusColumnVisible: boolean;
  isUSBasedHCPsVisible: boolean;
  usBasedHCPsLabel: string;
  usBasedHCPs: UsBasedHCPs[];
  isUSBasedHCPsTravelParameterVisible: boolean;
  usBasedHCPsTravelParameters: UsBasedHCPsTravelParameters[];
  isOUSBasedHCPsVisible: boolean;
  ousBasedHCPs: OusBasedHCPs[];
  isUSNonHCPsVisible: boolean;
  usNonHCPs: UsNonHCPs[];
  isUSNonHCPsTravelParameterVisible: boolean;
  usNonHCPsTravelParameters: UsNonHCPsTravelParameters[];
  isUSPatientCaregiversVisible: boolean;
  patientCaregivers: PatientCaregivers[];
  isPatientCaregiversTravelParameterVisible: boolean;
  patientCaregiversTravelParameters: PatientCaregiversTravelParameters[];
  isNonHCPsVisible: boolean;
  nonHCPsLabel: string;
  nonHCPs: NonHCPs[];
  isNonMedicalFieldBasedPersonnelsVisible: boolean;
  nonMedicalFieldBasedPersonnels: NonMedicalFieldBasedPersonnels[];
  isAdvisoryDescriptionInformationSectionVisible: boolean;
  descriptionOfServiceApprovalStatus: string;
  isAdvisoryStatusByWhenVisible: boolean;
  advisoryStatusByWhenLabel: string;
  advisoryStatusByWhen: string;
  isCommunicationInformationVisible: boolean;
  isApprovalTypeColumnVisible: boolean;
  isApprovalStatusColumnVisible: boolean;
  approvalStatusColumnHeaderLabel: string;
  communicationInformation: CommunicationInformation[];
  meetingStatus: string;
  gtmsId: string;
  mpcSupplierName: string;
  mpcEmails: string;
}

export interface EducationalItems {
  typeOfItem: string;
  description: string;
  unitRetailValue: number;
  quantity: number;
}
export interface UsBasedHCPs {
  facultyId: number;
  facultyName: string;
  cityState: string;
  organizationalContactsLabel: string;
  organizationalContacts: string;
  organizationalContactsTooltip: string;
  specialties: string;
  travelType: string;
  contractStatus: string;
}
export interface UsBasedHCPsTravelParameters {
  facultyName: string;
  travelParameter: string;
}
export interface OusBasedHCPs {
  facultyId: number;
  facultyName: string;
  city: string;
}
export interface UsNonHCPs {
  facultyId: number;
  facultyName: string;
  cityState: string;
  organizationalContactsLabel: string;
  organizationalContacts: string;
  organizationalContactsTooltip: string;
  specialties: string;
  travelType: string;
  contractStatus: string;
}
export interface UsNonHCPsTravelParameters {
  facultyName: string;
  travelParameter: string;
}
export interface PatientCaregivers {
  facultyId: number;
  facultyName: string;
  cityState: string;
  organizationalContactsLabel: string;
  organizationalContacts: string;
  organizationalContactsTooltip: string;
  specialties: string;
  travelType: string;
  contractStatus: string;
}
export interface PatientCaregiversTravelParameters {
  facultyName: string;
  travelParameter: string;
}
export interface NonHCPs {
  facultyName: string;
  cityState: string;
}
export interface NonMedicalFieldBasedPersonnels {
  nmpName: string;
  supervisorName: string;
  approvalStatus: string;
  nmpApprovedWhen: string;
}

export interface CommunicationInformation {
  description: string;
  communicationDate: string;
  version: string;
  communicationType: string;
  approvalType: string;
  medicalLegalorRegulatoryApprovalStatus: string;
  medicalLegalorRegulatoryApproverName: string;
  medicalLegalorRegulatoryApprovedWhen: string;
  communicationApprovalStatus: string;
  communicationApprovalName: string;
  communicationApprovalWhen: string;
}

export interface FieldLTSActivityInfosheetDetails {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  activityIdentity: string;
  brandLabel: string;
  brand: string;
  startDateTime: string;
  endDateTime: string;
  locationName: string;
  address: string;
  timeZone: string;
  isFacultiesSectionVisible: boolean;
  isContractStatusColumnVisible: boolean;
  faculties: FieldLTSFaculties[];
  isRepresentativeSectionVisible: boolean;
  representatives: FieldLTSRepresentatives[];
}

export interface FieldLTSFaculties {
  facultyName: string;
  facultyId: number;
  facultyConfirmed: string;
  travelRequired: string;
  contractStatus: string;
}
export interface FieldLTSRepresentatives {
  repName: string;
  islead: string;
  repEmail: string;
  costCenter: string;
  ioNumber: string;
}

export interface ActivityInfosheetDetails {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  supplier: string;
  activityIdentity: string;
  isPIDVisible: boolean;
  pid: string;
  statusLabel: string;
  status: string;
  isAssignedToVisible: boolean;
  assignedTo: string;
  isPWStatusVisible: boolean;
  pwStatus: string;
  startDateTime: string;
  endDateTime: string;
  type: string;
  description: string;
  brandLabel: string;
  brand: string;
  topic: string;
  isTravelOwnerVisible: boolean;
  travelOwner: string;
  isSpeakerConfirmedByRepVisible: boolean;
  speakerConfirmedByRep: string;
  isRecievedHowVisible: boolean;
  recievedHow: string;
  isNumberOfOfficeVisitVisible: boolean;
  numberOfOfficeVisit: string;
  recievedWhen: string;
  addedBy: string;
  locationName: string;
  address: string;
  isTimeZoneVisible: boolean;
  timeZone: string;
  isAudienceSectionVisible: boolean;
  expectedNumberOfAttendees: string;
  audienceSpecialties: string;
  surveysToBeMailed: string;
  isNotesColumnVisible: boolean;
  proposedDates: ProposedDates[];
  isAttendeeSectionVisible: boolean;
  attendees: Attendees[];
  isRepresentativeSectionVisible: boolean;
  isCostCenterColumnVisible: boolean;
  representatives: InfoSheetRepresentatives[];
  isPIDDistributionSectionVisible: boolean;
  pidDistributions: PidDistributions[];
  isFacultiesSectionVisible: boolean;
  isOrganizationalContactsColumnVisible: boolean;
  isProratedColumnVisible: boolean;
  isContractStatusColumnVisible: boolean;
  faculties: InfoSheetFaculties[];
  isTravelInformationSectionVisible: boolean;
  airport: string;
  distance: string;
  suggestedHotel: string;
  city: string;
  state: string;
  isCommentSectionVisible: boolean;
  comments: InfoSheetComments[];
  isVenueConfirmed: number;
}

export interface ProposedDates {
  label: string;
  alternateDateTime: string;
  timeZone: string;
  notes: string;
}
export interface Attendees {
  name: string;
  email: string;
}
export interface InfoSheetRepresentatives {
  repName: string;
  voiceMailMobile: string;
  isLead: string;
  territory: string;
  district: string;
  costCenter: string;
}
export interface PidDistributions {
  division: string;
  area: string;
  district: string;
  territoryName: string;
}
export interface InfoSheetFaculties {
  facultyId: number;
  facultyName: string;
  cityState: string;
  facultyType: string;
  organizationalContactsLabel: string;
  organizationalContacts: string;
  organizationalContactsTooltip: string;
  specialties: string;
  checkTypeDescription: string;
  amount: string;
  isProrated: string;
  contractStatus: string;
}
export interface InfoSheetComments {
  id: number;
  comments: string;
  addedBy: string;
  addedWhen: string;
}

export interface ActivityCalendarActivities {
  activityId: number;
  activityIdentity: string;
  activityURL: string;
  startDateTime: string;
  addressCityState: string;
  brand: string;
  leadRepName: string;
  cpsStatus: string;
  sbStatus: string;
}

export interface ActivityDirectionSearchPayloadType {
  activityNumber: string;
  pidNumber: string;
  supplierName: string;
  startDate: string;
  endDate: string;
  facultyLastName: string;
  facultyFirstName: string;
  facultyId: number;
  lillyPersonId: string;
  brandId: number;
  representativeLastName: string;
  representativeFirstName: string;
  city: string;
  stateId: number;
  countryId: number;
  pageIndex: number;
  pageSize: number;
  sortCol: string;
  sortOrder: string;
}

export interface DirectionActivitiesProps {
  isCPSStatusInformationIconVisible: boolean;
  cpsStatusHeaderText: string;
  activities: ActivityCalendarActivities[];
  totalRecordsCount: number;
  isLTSSupplier: boolean;
  isSBStatusHeaderTextVisible: boolean;
  isSBStatusInformationIconVisible: boolean;
  messageCode: number;
  message: string | null;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
}

export interface GetDirectionFormValues {
  toAddress1: string;
  toAddress2: string;
  toCity: string;
  toZip: string;
  toCountryId: number;
  toStateId: number;
  activityRole: string;
  individualId: number;
  fromAddress1: string;
  fromAddress2: string;
  fromCountryId: number;
  fromCity: string;
  fromStateId: number;
  fromZip: string;
  addressTypeId: string;
}

export interface EmailTemplateProps {
  alertTypeId: number;
  id: number;
  isAllChecked: boolean;
  isAllEnabled: boolean;
  isAllVisible: boolean;
  isAttendeeChecked: boolean;
  isAttendeeEnabled: boolean;
  isAttendeeVisible: boolean;
  isBothChecked: boolean;
  isBothEnabled: boolean;
  isBothVisible: boolean;
  isExistingConfiguration: boolean;
  isFacultyChecked: boolean;
  isFacultyEnabled: boolean;
  isFacultyVisible: boolean;
  isLeadRepresentativeChecked: boolean;
  isLeadRepresentativeEnabled: boolean;
  isLeadRepresentativeVisible: boolean;
  isRepresentativeChecked: boolean;
  isRepresentativeEnabled: boolean;
  isRepresentativeVisible: boolean;
  lastSendWhen: string | null;
  name: string;
  title: string | null;
}

export interface SendCorrespondenceProps {
  activityIdentity: string;
  activityStatus: string;
  activityStatusId: number;
  endDateTime: string;
  facultyCount: number;
  inviteeCount: number;
  isActivityDetailSectionVisible: boolean;
  isActivityStatusColumnVisible: boolean;
  isApprovalStatusColumnVisible: boolean;
  isBrandSendCorrespondenceRequired: boolean;
  isEndDateTimeColumnVisible: boolean;
  isLocationStatusColumnVisible: boolean;
  isPleaseSelectOptionsVisible: boolean;
  isSpeakerStatusColumnVisible: boolean;
  isStartDateTimeColumnVisible: boolean;
  isSupplierColumnVisible: boolean;
  locationStatus: string | null;
  locationStatusId: number;
  speakerStatus: string;
  speakerStatusColumnHeaderLabel: string;
  speakerStatusId: number;
  startDateTime: string;
  supplier: string | null;
}

export interface SendCorrespondenceDropdownListType {
  controlId: string;
  id: string;
  name: string;
  selectedIds: string | null;
}

export interface SendCorrespondenceSpeakers {
  speakerName: string;
  unreservedFunds: boolean;
  emailAddress: string;
  facultyId: number;
  lillyUniqueId: string;
}
export interface SendCorrespondenceActivityData {
  activityNumber: string;
  activityType: string;
  description: string;
  brandDiseaseState: string;
  startDateTime: string;
  isSpeakersVisible: boolean;
  speakers: SendCorrespondenceSpeakers[];
  isInviteesVisible: boolean;
  invitees: [];
}
export interface SendCorrespondenceEmailProps {
  from: string;
  isToLabel: boolean;
  to: string;
  isCCLabel: boolean;
  cc: string;
  isBCCLabel: false;
  bcc: string;
  subject: string;
  bodyText: string;
  isFileUploadVisible: boolean;
  isConfirmationEmailLabelVisible: boolean;
  confirmationEmailLabel: string | null;
  isCommentsVisible: boolean;
  isSecondBodyTextVisible: boolean;
  secondBodyText: string | null;
  ddLs: SendCorrespondenceDropdownListType[];
  isViewCalendarInviteHyperlinkVisible: boolean;
  viewCalendarSubject: string;
  viewCalendarLocation: string;
  viewCalendarBodyText: string;
  isBIImageVisible: boolean;
  isLillyImageVisible: boolean;
  isSendButtonVisible: boolean;
  attachmentFileSizeLimit: number;
}

export interface ActivityCalendarActivitiesProps {
  isCPSStatusColumnInfoIconVisible: boolean;
  cpsStatusColumnHeaderLabel: string;
  calendarActivities: ActivityCalendarActivities[];
  totalRecordsCount: number;
  messageCode: number;
  message: string | null;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
}

export interface BrandActivityBrandListParams {
  id: number;
  name: string;
  status: boolean;
  supplierId: number;
  contractTypeId: number;
}
export interface BrandActivityAllBrandListParams {
  id: number;
  brandName: string;
  status: boolean;
  supplierId: number;
  isTrainingBrand: boolean;
}
export interface BrandActivityNameIdListParams {
  id: number;
  name: string;
}
export interface BrandActivityPIDDistributionListParams {
  division: string;
  area: string;
  districtId: string;
  districtName: string;
  territoryId: string;
  territoryName: string;
  fieldType: number;
  fieldValue: string;
}
export interface BrandActivityActivityDescriptionsListParams {
  id: number;
  name: string;
  activityTypeId: number;
  activitySubTypeId: number;
  status: boolean;
  ruleActivityTypeId: number;
  isTraining: boolean;
  brandIds: string;
  isARFActivityDescription: boolean;
  faTypeId: number;
  meetingTypeId: number;
  mappedCostElement: string;
  attendeeTypeIds: string;
  isARFParticipantRequired: boolean;
  isMedicalConsultingService: number;
  meetingId: string;
  isBNARequired: number;
  isConsultantRoleRequired: number;
  epri: boolean;
  isFacultyRecruitmentSpecialty: boolean;
  isVMSSpanishTranslation: boolean;
  defaultEndTimeHour: number;
  newAudiencePageEnabled: boolean;
  ignoreActivityDuration: boolean;
  isOUSGlobal: boolean;
  isOUS: boolean;
  isAdvisingActivity: boolean;
  isBrandPIDDistribution: boolean;
  activitySeries: boolean;
  proactiveBookingServices: boolean;
  advisingServiceDescription: boolean;
  isLMSDescription: number;
}
export interface FieldActivityParams {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  activityInformation: ActivityInfoParams;
  autoCancelConfigurationId: number;
  isLTSSupplier: boolean;
  isVMSSupplier: boolean;
  paymentMode: boolean;
  activityStatusId: number;
  activityAddedWhen: string;
  isLillyCloseOutActivity: boolean;
  travelValidationRequired: boolean;
  multipleSeries: FieldActivityMultipleSeries | null;
  facultyInformation: FieldActivityFacultyInformation;
  cancelledByWhomId: number;
  cancellationReasonId: number;
  speakerNotifiedOfCancellation: boolean;
  dateSpeakerNotified: string;
  conferenceDialInNumber: string;
  conferenceAccessCodes: string;
  isCapPaymentProductionDate: boolean;
  clinicalConversationDescriptionIds: string;
  isActivityClosed: number;
  sbStatus: number;
  isProgramCloseOutEnabledForCancelledFieldFDA: boolean;
  isActivityHeaderDetailVisible: boolean;
  activityDetails: ActivityHeaderDetailParams;
  isActivityDetailSectionVisible: boolean;
  activityDateTime: FieldActivityDateAndTimeParams;
  representative: FieldActivityRepresentatives;
  activityLocation: FieldActivityActivityLocation;
  withinWindowPeriod: boolean;
  isConfirmedDate: boolean;
  isVMSActivityDateChanged: boolean;
  isCopyActivity: boolean;
  activityAudience: ActivityAudienceParams;
  speakerTravel: FieldActivitySpeakerTravel;
  activityComments: ActivityActivityComments[];
  isDateChanged: boolean;
  isFacultyChanged: boolean;
  hiddenCloseCancelActivityFromFaculty: boolean;
  salesForceRuleStatus: number;
  isGoogleAPIDown: boolean;
  addedBySupplierId: number;
  mpcSupplierId: number;
  lillyActivityOwnerEmailId: string;
  lillyActivityOwnerNameTitle: string;
  addedByProfileId: number;
  advisingTypeId: number;
  isActivityHCPRegistrationEnabled: boolean;
  travelOwner: string;
  activityUpdatedWhen: string | null;
  occurredWhen: string | null;
  canOccurProgram: boolean;
  programValidation: ActivityProgramValidationParams;
  brandActivityTimeZoneId: string | null;
  isAddedFromCPP: boolean;
  originalActivityStatusId: number;
  originalDescriptionOfService: string;
  descriptionOfService: string;
  isVMSActivityFacultyChanged: boolean;
  planHonorariumTotal: number | null;
  planExpenseTotal: number | null;
  originalTotalPlannedAmount: number;
  totalPlannedAmount: number;
  totalActualAmount: number;
  materialOrderDetailsList: MaterialOrderDetailsListParams;
  advisoryDescription?: AdvisoryDescriptionParams;
  brandActivityInformation: BrandActivityInformationParams;
  sponsorEmail1: string;
  sponsorContact1: string;
  sponsorContact1ProfileAndTrainingStatus: string;
  sponsorEmail2: string;
  sponsorContact2: string;
  sponsorContact2ProfileAndTrainingStatus: string;
  sponsorEmail3: string;
  sponsorContact3: string;
  sponsorContact3ProfileAndTrainingStatus: string;
  activityTopics: BrandActivityActivityTopicsParams[];
  compensationResponse: number | null;
  activityFinance: BrandActivityFinanceParams;
  activityMeetingInfo: BrandActivityMeetingInfoParams;
  commentTypeId: number;
  comment: string;
  skipLillyMissingApiCall: number;
  correspodenceName: string | null;
  correspodenceTitle: string | null;
  budgetStatus: number;
  logisticsAmount: number | null;
  eduAndPRIAmount: number | null;
  csLevel1ApprovalStatus: number;
  csLevel2ApprovalStatus: number;
  csLevel3ApprovalStatus: number;
  isBudgetExceeded: boolean;
  approvalStatusId: number | null;
  feedbackActivityAlertInfos: FeedbackActivityAlertInfosParams[];
  feedbackActivityInformation: FeedBackActivityInformationParams;
  locationInformation: LocationInformationParams;
  originalParticipantsInformation: FeedBackParticipantsInformationParams;
  participantsInformation: FeedBackParticipantsInformationParams;
  isDraftActivity: boolean;
  brandActivityTypeId: number;
  isARFActivity: boolean;
  originalCSLevel1ApprovalStatus: number;
  originalCSLevel2ApprovalStatus: number;
  originalCSLevel3ApprovalStatus: number;
  isFacultyCountAlert: boolean | null;
  originalIsFacultyCountAlert: boolean | null;
  originalActualAmount: number;
  masterGridId: number;
  isPendingAlerts: boolean;
  isARFParticipantsPresent: boolean;
  isLocationInfoSubmitted: boolean;
  isParticipantInfoSubmitted: boolean;
  isAdvisoryInfoSubmitted: boolean;
}

export interface BrandActivityMeetingInfoParams {
  id: number;
  meetingId: string;
  foodAndBeverageCost: number;
  hotelCost: number;
  transportationCost: number;
  totalAmount: number;
  epriCost: number;
  totalAttendees: number;
  userDetails: string;
}
export interface BrandActivityFinanceParams {
  approvedDeniedBy: string;
  approvedDeniedWhen: string;
  status: number;
  deletedCostCenterList: string;
  activityCostCenters: BrandActivityCostCentersParams[];
  costElement: string;
  costDescription: string;
}
export interface BrandActivityCostCentersParams {
  primaryId: number;
  costCenterKey: string;
  costCenterPercent: string;
  ioNumber: string;
  ioDescription: string;
  id?: number | string;
}
export interface BrandActivityActivityTopicsParams {
  id: number;
  brandId: number;
  topicId: number;
  status: boolean;
}
export interface BrandActivityInformationParams {
  isPrintableViewHyperlinkVisible: boolean;
  isPaymentRequestHyperlinkVisible: boolean;
  isCheckStatusHyperlinkVisible: boolean;
  isGrantSupplierAccessHyperlinkVisible: boolean;
  isActivityHistoryHyperlinkVisible: boolean;
  isHCPRegistrationAdminHyperlinkVisible: boolean;
  isViewQRCodeHyperlinkVisible: boolean;
  activityIdentity: string;
  pid: string;
  isActivityStatusLabelVisible: boolean;
  activityStatusLabel: string;
  isOccurredVisible: boolean;
  isOccurredCheckboxEnabled: boolean;
  isOccurred: boolean;
  occurredDate: string;
  isCancelledVisible: boolean;
  isCancelledCheckboxEnabled: boolean;
  isCancelled: boolean;
  cancelledDate: string;
  isInactiveOptionVisible: boolean;
  isInactiveCheckboxEnabled: boolean;
  isInactive: boolean;
  activityTypeId: number;
  activityType: string;
  isBudgetStatusVisible: boolean;
  budgetStatus: string;
  isRSVPEnabledVisible: boolean;
  rsvpEnabled: string;
  isTLMapActivityVisible: boolean;
  isTLMapActivityEnabled: boolean;
  isTLMapActivity: boolean;
  isTLMapTextVisible: boolean;
  isTLMapTextEnabled: boolean;
  tlMapText: string;
  isTLMapValidationNoteVisible: boolean;
  isTLMapSuccessNoteVisible: boolean;
  tlMapSuccessNote: string;
  isTLMapSearchButtonVisible: boolean;
  isTLMapSearchButtonEnabled: boolean;
  isApprovalStatusVisible: boolean;
  approvalStatus: string;
  isTLMapTopicVisible: boolean;
  tlMapTopic: string;
  isActivityOwnerVisible: boolean;
  activityOwner: string;
  isBusinessNeedVisible: boolean;
  businessNeed: string;
  isBrandEnabled: boolean;
  brandLabel: string;
  brands: BrandActivityBrandListParams[];
  brandId: number;
  originalBrandId: number;
  allBrands: BrandActivityAllBrandListParams[];
  isActivityDescriptionEnabled: boolean;
  activityDescriptions: BrandActivityActivityDescriptionsListParams[];
  originalActivityDescriptionId: number;
  activityDescriptionId: number;
  isSupplierEnabled: boolean;
  suppliers: BrandActivityNameIdListParams[];
  supplierId: number;
  isStartDateTimeEnabled: boolean;
  originalStartDateTime: string | null;
  startDateTime: string | null;
  isEndDateTimeEnabled: boolean;
  originalEndDateTime: string | null;
  endDateTime: string | null;
  timeZones: BrandActivityNameIdListParams[];
  brandActivityTimeZoneId: string;
  isTopicEnabled: boolean;
  topic: string;
  isTopicTrainingRequirementsButtonVisible: boolean;
  isTopicTrainingRequirementsButtonEnabled: boolean;
  isActivitySeriesButtonEnabled: boolean;
  isBusinessUnitEnabled: boolean;
  businessUnits: BrandActivityNameIdListParams[];
  businessUnitId: number;
  isCVentMeetingEnabled: boolean;
  isCVentMeeting: boolean;
  cmsMeeting: string;
  isCVentMeetingIdVisible: boolean;
  isCVentMeetingIdEnabled: boolean;
  cVentMeetingId: string;
  cVentMeetingIdToolTip: string;
  isMPCVisible: boolean;
  isMPCEnabled: boolean;
  mpcSuppliers: BrandActivityNameIdListParams[];
  mpcSupplierId: number;
  isMPCEmailSectionVisible: boolean;
  mpcEmails: string;
  isEPRISectionVisible: boolean;
  isEPRISectionEnabled: boolean;
  epriInfo: {
    willEPRIItemsGiven: boolean;
    epriItems: EPRIItemParams[];
    epriItem1Id: number;
    epriItem1Text: string;
    epriItem1Value: number;
    epriItem2Id: number;
    epriItem2Text: string;
    epriItem2Value: number;
  };
  isSpanishTranslationVisible: boolean;
  isSpanishTranslation: boolean;
  isFacultySpecialtiesVisible: boolean;
  specialties: SpecialtiesParams[];
  specialtyId1: number;
  specialtyId2: number;
  specialtyId3: number;
  isPIDExportToExcelButtonVisible: boolean;
  isAddPIDAccessButtonVisible: boolean;
  isPIDDistributionSectionVisible: boolean;
  pidDistributions: BrandActivityPIDDistributionListParams[];
  isPaymentTrackingHeaderVisible: boolean;
  isNoPaymentsVisible: boolean;
  isNoPaymentsEnabled: boolean;
  isNoPayments: boolean;
  isAcknowledgementHeaderVisible: boolean;
  isCheckCertificateEnabled: boolean;
  isCheckCertificate: boolean;
  userDetails: string;
  originalSupplierId: number;
}
export interface AdvisoryDescriptionParams {
  originalDescriptionOfService: string;
  descriptionOfService: string;
  descriptionforChair: string;
  descriptionforPresenter: string;
  descriptionforModerator: string;
}
export interface MaterialOrderDetailsListParams {
  hasFacultyConfirmedJustNow: boolean;
  materialOrderDetails: MaterialOrderDetailsParams[];
}
export interface ActivityActivityComments {
  id: number;
  supplier: string;
  addedBy: string;
  addedWhen: string;
  comments: string;
  commentType: string;
  commentTypeId: number;
  isDeleteButtonVisible: boolean;
}

export interface ActivityProgramValidationParams {
  isCloseoutCertified: boolean;
  complianceQuestionsWithAnswers: ComplianceQuestionsWithAnswersParams[];
  isNewLayout: boolean;
  isProgramCoachingToolReviewed: boolean;
  requestorId: number;
  requestorName: string | null;
  programCoachingToolCheckboxAvailable: boolean;
  isActivityCompliant: boolean;
}
export interface ActivityAudienceValidationParams {
  activityUpdatedWhen: string;
  activityDescriptionId: number;
  isAudienceValidationCompleted: boolean;
  isCompletedProgramValidationMessageVisible: boolean;
  isAttendeeDataCorrectEnabled: boolean;
  isAttendeeDataCorrect: boolean;
  totalEsignatureCount: number;
  isUpdateAttendeesButtonVisible: boolean;
  isCompleteValidationButtonVisible: boolean;
  isCompleteValidationButtonEnabled: boolean;
  isNextButtonVisible: boolean;
  isNextButtonEnabled: boolean;
  messageCode: number;
  message: string | null;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
}
export interface ComplianceQuestionsWithAnswersParams {
  questionId: number;
  questionText: string;
  answerId: number;
  answers: DropdownList[];
}

export interface ActivityAudienceParams {
  participantCount: string | number;
  attendeeInformations: ActivityAudienceAttendeeInformations[];
  audienceSpecialities: ActivityAudienceSpecialities;
  activityInvitees: ActivityAudienceActivityInvitees[];
  activityRSVPInfos: ActivityRSVPInfosListParams[];
  activitySISInfos: ActivitySISInfosListParams[];
  activityReceipts: ActivityReceiptsParams[];
  materialOrderDetails: MaterialOrderDetailsParams[] | null;
}

export interface SpecialtyListParams {
  id: number;
  name: string;
  status: number;
}
export interface RequestFormInquiriesParams {
  requestFormId: number;
  firstName: string;
  lastName: string;
  role: string;
  additionalDetails: RFTAdditionalDetailsParams[];
  receivedOn: string;
}
export interface RFTAdditionalDetailsParams {
  fieldId: string;
  fieldValue: string;
}
export interface ActivitySISInfosListParams {
  sisId: number;
  fileName: string;
  displayName: string;
  sisStatusId: number;
  sisStatus: string;
  addedByName: string;
  addedWhen: string;
  isAutoGenerated: boolean;
  isDeleted: boolean;
  isUpdated: boolean;
  notes: string;
}
export interface ActivityRSVPInfosListParams {
  rsvpRegistrantId: number;
  firstName: string;
  lastName: string;
  practiceAddress: string;
  email: string;
  company: string;
  city: string;
  state: string;
  zipCode: string;
  reconciliationStatus: boolean;
  reconciliationCount: number;
  specialty: string;
  credentials: string;
  licenseState: string;
  licenseNumber: string;
  status: boolean;
  isValid: boolean;
  isDeleted: boolean;
  rsvP_RepresentativeName: string;
  npiNumber?: string;
}
export interface ActivityAudienceAttendeeInformations {
  activityAttendeeId: number;
  productRepresentativeId: number;
  facultyId: number;
  firstName: string;
  lastName: string;
  mi: string;
  degree: string;
  state: string;
  city: string;
  speciality1: string;
  speciality2: string;
  addedFromAudience: number;
  attendanceStatusId: number;
  stateId: number;
  status: number;
  isModified: number;
  degreeId: string;
  attendeeStatusId: number;
  attendeeType: number;
  requestedBy: number;
  attendeeMeetingTypeId: number;
  lillyPersonId: string;
  address1: string;
  addedBy: number;
  zipCode: string;
  requestedByName: string;
  addressType: string;
  addedSystem: number;
  email: string;
  emailLastUpdated: string;
  me: string;
  slsPrescriberId: string;
  attendeeSpecialtyId: number;
  attendeeSpecialty: string;
}
export interface ActivityAudienceSpecialities {
  specialtyIds: string;
  specialties: string | null;
}
export interface ActivityAudienceActivityInvitees {
  rowId: number;
  supplierId: number;
  firstName: string;
  middleName: string;
  lastName: string;
  salutation: string;
  specialty: string;
  credential: string;
  clientId: string;
  enterpriseId: string;
  referenceId: number;
  invitedById: number;
  inviteeTypeId: number;
  inviteeType: string;
  isWriteIn: boolean;
  isDeleted: boolean;
  inviteeSortOrder: number;
  address1: string;
  address2: string;
  city: string;
  stateShortName: string;
  zipCode: string;
  isPartnerRep: boolean;
  isLeadRep: boolean;
  productRepresentativeId: number;
  addedWhen: string;
  isDeleteVisible: boolean;
  isDeleteAllowed: boolean;
  id: number;
  activityInviteeId: number;
  isAttended: boolean | null;
  isMealConsumed: boolean | null;
  isSigned: boolean;
  isSignedCount: boolean;
  addedBy: number;
  isEditProfileAllowed: boolean;
  licenseStateId: number;
  stateId: number;
  licenseNumber: string;
  specialtyId: number;
  npiNumber: string;
  positionId: number;
  organization: string;
  territoryId: string;
  repId: number;
  licenseState: string;
  rsvP_RegistrantId: number;
  isFromBreadcrumb: string;
  isPaperSignaturePresent: boolean;
  isSignatureDeclined: boolean;
  isUpdated: boolean;
  repAddedBy: string;
  isAddedByAdminSuperUser: boolean;
  email: string;
  phoneNumber: string;
  isNPIStateLicenseAvailable: boolean;
  inviteeGUID: string;
  isCheckInButtonVisible: boolean;
  inviteeEmail: string;
  isTarget: boolean;

  invitedByName: string;
  isSignedPaperEnabled: boolean;
  isUnabledToSignedEnabled: boolean;
}
export interface FieldActivityFacultyInformation {
  isSendAfterAllApprovals: boolean;
  isSentLater: boolean;
  sendLaterDate: string;
  actualSendLaterDate: string;
  faculties: FieldActivityFacultyParams[];
  facultyOrganizationalContactInfos: FacultyOrganizationalContactInfosParams[];
  isFieldActivity?: boolean;
  isVisibleTravelRequiredFields?: boolean;
  isEnabledTravelRequiredFields?: boolean;
  isMPCAssigned?: boolean;
  isSuperUser?: boolean;
}
export interface FieldActivityAttendeeInformations {
  requestedBy: number | null;
  requestedByName: string | null;
  isModified: number;
  attendeeType: number;
  facultyId: number;
  attendeeStatusId: number;
  status: number;
}
export interface FieldActivityRepresentatives {
  costElement: string;
  costDescription: string;
  representatives: FieldActivityRepresentativesList[];
  deletedRepList: string;
  originalLeadRepSupplierId: number;
  pidDistributions: FieldActivityPiDs[];
}
export interface SaveFieldActivityRepresentatives {
  activityId: number;
  supplierId: number;
  brandId: number;
  activityDescriptionId: number;
  isDateChanged: boolean;
  isFacultyChanged: boolean;
  activityDateTime: FieldActivityDateAndTimeParams;
  representative: {
    costElement: string;
    costDescription: string;
    originalLeadRepSupplierId: number | undefined;
    representatives: FieldActivityRepresentativesList[];
    deletedRepList: string;
    pidDistributions: FieldActivityPiDs[];
  };
  profileId: number;
  userTypeId: number;
}
export interface FieldActivityCostDescriptionsParams {
  id: number;
  name: string;
}
export interface FacultyOrganizationalContactInfosParams {
  id: number;
  roleId: number;
  facultyId: number;
  firstName: string;
  lastName: string;
  name: string;
  userId: string;
  status: string;
  email: string;
  phone: string;
}
export interface FieldActivityFacultyParams {
  aftId: number;
  facultyId: number;
  lastName: string;
  middleInitial: string;
  firstName: string;
  rank: number;
  facultyName: string;
  proactiveBookingTravelLabel: string;
  subStatus: string;
  facultyApprovalStatusID: number;
  facultyApprovalStatus: string;
  participantTypeId: number;
  city: string;
  state: string;
  cityState: string | null;
  facultyTypeId: number;
  facultyType: string;
  organizationalContactsLabel: string;
  organizationalContactsTooltip: string;
  organizationalContacts: string;
  specialty1: string;
  paymentAmount: number;
  expenseAmount: number;
  miles: string;
  annualContractSigned: string;
  isTravelTypeEnabled: boolean;
  isResetHCPRegistrationHyperlinkVisible: boolean;
  isSaveIconVisible: boolean;
  isViewItineraryLabelVisible: boolean;
  isViewItineraryHyperlinked: boolean;
  viewItineraryLabel: string;
  viewItineraryTitle: string;
  pnr: string;
  isTravelParameterHyperlinkVisible: boolean;
  travelParameterHyperlinkLabel: string;
  travelParameter: string;
  isFacultyConfirmed: boolean | null | undefined;
  ov: number;
  isPNRGenerated: boolean;
  isCRP: boolean;
  transactionTypeId: number;
  oldOV: number;
  salutation: string;
  credentials: string;
  oldPaymentAmount: number;
  checkType: number;
  isReissueCheckType: number;
  isTravelRequiredKey: number;
  travelValidationRequired: boolean;
  oldEstimatedTravelAmount: number;
  estimatedTravelAmount: number;
  travelDistance: number;
  isTravelAmountConfirmed: boolean;
  honorariumTravelRules: string;
  isManualModifiedTravelAmount: boolean;
  isAmountDecreasedForFMV: boolean;
  address1: string;
  address2: string;
  countryId: number;
  zipCode: string;
  travelIndicator: boolean;
  requiresCSApproval: boolean;
  reasonForRevHonorarium: string;
  amountRevisedToZeroWhen: string;
  isFundUnReserved: boolean;
  checkNumber: string;
  honorariumAmount: number;
  facultyAddedWhen: string;
  checkStatusDescription: string;
  isDOL: boolean;
  participationRoleId: number;
  specialty2: string;
  lillyPersonId: string;
  contractSigned: string;
  oldRevisedAmount: number | null;
  activityDuration: number;
  prepTime: number;
  originOUS: number;
  destinationOUS: number;
  facultyStatus: number;
  facultyIdentificationRuleId: number;
  isZeroPayment: boolean;
  specialties: string;
  travelType: string;
  contractStatus: string;
  checkTypeDescription: string;
  amount: string;
  isProrated: string;
  facultyConfirmed: string;
  travelRequired: string;
  facultyApprovalStatusId: number;
  travelTypeIds: string;
  isVisible: boolean;
  isSpeakerVerballyConfirmed: boolean | null;
  honorariumExceptionType: number;
  oldHonorariumExceptionType: number;
  honorariumRemovalReason: string | null;
  productId: number | null;
  caregiverFirstName: string | null;
  caregiverLastName: string | null;
  isCaregiverUpdated: boolean;
  isOUSTravelAllowanceNA: boolean;
  isSupplementFieldDisabled?: boolean;
  enterpriseId: string | null;
}
export interface FieldActivityActivityLocation {
  locationName: string;
  locationContactName: string;
  originalLocationAddress1: string;
  locationAddress1: string;
  originalLocationAddress2: string;
  locationAddress2: string;
  originalLocationCity: string;
  locationCity: string;
  originalLocationStateId: number;
  locationStateId: number;
  originalLocationZipCode: string | null;
  locationZipCode: string | null;
  originalLocationCountryId: number;
  locationCountryId: number;
  conferenceDialInNumber: string;
  conferenceAccessCodes: string;
  videoConferenceWebLink: string;
  videoConferenceMeetingNumber: string;
  videoConferenceMeetingPassword: string;
  isMealVisible: boolean;
  isMealsProvided: boolean | null | undefined;
  isVenueConfirmed: number | null;
  isLocationGoogleVerified: boolean;
  isAutoMeetingLink: boolean;
  isAVTechRequired: boolean;
  avTechDetail: string;
  isAVEquipmentRequired: boolean;
  avEquipmentDetail: string;
  isCreditCardRequired: boolean | null | undefined;
  avTechName: string;
  avTechEmail: string;
  avTechPhone: string;
  osmName: string;
  osmEmail: string;
  osmPhone: string;
}

export interface TravelParamType {
  activityMeetingInformationId?: number;
  facultyId?: number;
  pnr?: string;
  travelParameter?: string | null;
}

export interface HonorariumTravelRulesType {
  honorariumTravelRulesId: number;
  facultyId: number;
  distanceStartRange: number;
  distanceEndRange: number;
  amount: number;
}
export interface FacultySearchOrganizationalContactInfosType {
  id: number;
  roleId: number;
  facultyId: number;
  firstName: string;
  lastName: string;
  name: string;
  userId: number;
  status: string;
  email: string;
  phone: string;
}

export interface FieldActivityLocationStatesList {
  id: number;
  name: string;
  countryId: number;
  shortName: string;
}
export interface FieldActivityLocationCountryList {
  id: number;
  name: string;
}
export interface FieldActivityPiDs {
  rowId: number | string;
  division: string;
  area: string;
  districtId: string;
  districtName: string;
  territoryId: string;
  territoryName: string;
  fieldType: number;
  fieldValue: string;
}

export interface FieldActivityRepresentativesList {
  ioNumber: string;
  repSupplierId: number;
  id: number;
  representativeKey: number;
  isRepBlocked: boolean;
  representativeName: string;
  lastName: string;
  firstName: string;
  bestContactNumber: string;
  supplierShortName: string;
  supplierName: string;
  email: string;
  isPRRepresentative: boolean;
  isCostCenterRuleApplied: boolean;
  ruleAppliedCostCenter: string;
  divisionId: string;
  hasCreditCard: boolean;
  isLead: boolean;
  addressPortablePhone: string;
  confirmedCellPhoneNo: string;
  faceTimeAccountId: string;
  isFaceTimeChanged: boolean;
  isCellPhoneForFutureUse: boolean;
  isConfirmedPhoneNoChanged: boolean;
  territoryId: string;
  territoryName: string;
  costCenterKey: string;
  originalCostCenterKey: string;
  isCostCenterModified: true;
  costCenterPercent: string;
  repId: number;
  isRepPartner: boolean;
}
export interface SearchPIDDistributionParams {
  distributionId: number;
  additionalSearchCriteria: string;
  pageIndex: number;
  pageSize: number;
  pidDistributions: FieldActivityPiDs[];
}
export interface FieldActivitySpeakerTravel {
  closestAirport: string;
  distance: string;
  suggestedHotel: string;
  city: string;
  state: string | number | null;
}
export interface FieldActivityMultipleSeries {
  isFirst: boolean | undefined;
  firstActivityId: number | string | undefined;
}

export interface ActivityFinanceResponseParams {
  isBrandApproverSectionVisible: boolean;
  approvedDeniedBy: string | null;
  isBrandApprovedEnabled: boolean;
  isBrandApproved: boolean | null;
  approvedDeniedWhen: string | null;
  isBudgetSectionVisible: boolean;
  isPlannedEnabled: boolean;
  isBudgetApprovedEnabled: boolean;
  budgetStatus: number;
  approvalStatus: string;
  isActivityCostCentersEnabled: boolean;
  isCostDescriptionEnabled: boolean;
  costDescriptionLabel: string;
  costDescriptions: DropdownList[];
  isPayOnceAttendedSectionVisible: boolean;
  isBudgetExempted: boolean;
  messageCode: number;
  message: string | null;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  defaultCostElement: string;
}

export interface ActivityInfoParams {
  isCancelActivityHyperlinkEnabled: boolean;
  isCancelActivityHyperlinkVisible: boolean;
  isPaymentRequestHyperlinkVisible: boolean;
  isCheckStatusHyperlinkVisible: boolean;
  isPrintableViewHyperlinkVisible: boolean;
  isGrantSupplierAccessHyperlinkVisible: boolean;
  isActivityHistoryHyperlinkVisible: boolean;
  isHCPRegistrationAdminHyperlinkVisible: boolean;
  isViewQRCodeHyperlinkVisible: boolean;
  activityIdentity: string;
  pid: string;
  isFacultyStatusVisible: boolean;
  facultyStatusLabel: string;
  facultyStatus: string;
  isLocationStatusVisible: boolean;
  locationStatusText: string;
  isActivityStatusVisible: boolean;
  isOccurredCheckboxEnabled: boolean;
  isOccurred: boolean;
  occurredDate: string;
  isCancelledCheckboxEnabled: boolean;
  isCancelled: boolean;
  cancelledDate: string;
  isInactiveOptionVisible: boolean;
  isInactiveCheckboxEnabled: boolean;
  isInactive: boolean;
  occurredByWhen: string;
  isDMApprovalStatusVisible: boolean;
  originalDMApprovalStatus: string;
  dmApprovalStatus: string;
  isCurrentAcceptedInviteesVisible: boolean;
  currentAcceptedInvitees: number | null;
  isManagementApprovalReceivedVisible: boolean;
  isManagementApprovalReceivedEnabled: boolean;
  isManagementApprovalReceived: boolean;
  managementApprovalReceivedWhen: string;
  isActivityTypeEnabled: boolean;
  activityTypes: ActivityInfoActivityTypes[];
  activityTypeId: number;
  isActivitySeriesButtonVisible: boolean;
  isActivitySeriesButtonEnabled: boolean;
  isBrandEnabled: boolean;
  brandLabel: string;
  brands: ActivityInfoBrands[];
  brandId: number;
  originalBrandId: number;
  isActivityDescriptionEnabled: boolean;
  activityDescriptions: ActivityInfoActivityDescriptions[];
  activityDescriptionId: number;
  originalActivityDescriptionId: number;
  isTopicEnabled: boolean;
  topics: ActivityInfoTopics[];
  topicId: number;
  isAssignedToVisible: boolean;
  isAssignedToEnabled: boolean;
  assignedTos: ActivityInfoAssignedTos[];
  assignedToId: number;
  supplierId: number;
  originalSupplierId: number;
  isSupplierEnabled: boolean;
  suppliers: ActivityInfoSuppliers[];
  isReceivedHowEnabled: boolean;
  isReceivedHowVisible: boolean;
  receivedHows: ActivityInfoReceivedHows[];
  receivedHowId: string;
  isSpanishTranslationVisible: boolean;
  isSpanishTranslation: boolean;
  isCVentMeetingVisible: boolean;
  isCVentMeetingEnabled: boolean;
  isCVentMeeting: boolean;
  isCVentMeetingIdVisible: boolean;
  isCVentMeetingIdEnabled: boolean;
  cVentMeetingId: string;
  cVentMeetingIdToolTip: string;
  isBusinessUnitVisible: boolean;
  isBusinessUnitEnabled: boolean;
  businessUnits: ActivityInfoBusinessUnits[];
  businessUnitId: number;
  cmsMeeting: string;
  isReceivedInTimeVisible: boolean;
  isReceivedInTimeEnabled: boolean;
  isReceivedInTime: boolean;
  receivedWhen: string;
  isSponsoringPhysicianVisible: boolean;
  isSponsoringPhysicianEnabled: boolean;
  sponsoringPhysician: string;
  isMEVisible: boolean;
  isMEEnabled: boolean;
  me: string;
  isEPRISectionVisible: boolean;
  isEPRISectionEnabled: boolean;
  epriInfo: {
    willEPRIItemsGiven: boolean;
    epriItems: EPRIItemParams[];
    epriItem1Id: number;
    epriItem1Text: string;
    epriItem1Value: number;
    epriItem2Id: number;
    epriItem2Text: string;
    epriItem2Value: number;
  };
  isBusinessJustificationsVisible: boolean;
  businessJustifications: ActivityInfoBusinessJustifications[];
  isFacultySpecialtiesVisible: boolean;
  specialties: SpecialtiesParams[];
  specialtyId1: number;
  specialtyId2: number;
  specialtyId3: number;
  isNoPaymentsVisible: boolean;
  isNoPaymentsEnabled: boolean;
  isNoPayments: boolean;
  isCheckCertificateEnabled: boolean;
  isCheckCertificate: boolean;
  userDetails: string;
}

export interface ActivityInfoActivityTypes {
  id: number;
  description: string;
}
export interface ActivityInfoBrands {
  id: number;
  brandName: string;
  status: boolean;
  supplierId: number;
}
export interface BrandActivityBrands {
  id: number;
  name: string;
  status: boolean;
  supplierId: number;
  contractTypeId: number;
}
export interface ActivityInfoActivityDescriptions {
  id: number;
  name: string;
  brandIds: string;
  activityTypeId: number;
  brandId: number;
  activitySubTypeId: number;
  newAudiencePageEnabled: boolean;
  isFacultyRecruitmentSpecialty: boolean;
  isVMSSpanishTranslation: boolean;
  epri: boolean;
  isGTMSIdDisplay: boolean;
  meetingId: string;
  defaultEndTimeHour: number;
  supplierName: string;
}
export interface ActivityInfoTopics {
  id: number;
  name: string;
  brandId: number;
  status: boolean;
  trBrandId: number;
  trBrandStatus: string | null;
  activityDescriptionIds: string;
}
export interface ActivityInfoAssignedTos {
  id: string;
  operatorName: string;
  supplierId: number;
  operatorId: number;
}
export interface ActivityInfoReceivedHows {
  id: string;
  name: string;
}
export interface ActivityInfoBusinessUnits {
  id: string;
  name: string;
}
export interface ActivityInfoBusinessJustifications {
  configurationId: number;
  businessJustification: string;
  isCheckboxEnabled: boolean;
  isChecked: boolean;
}
export interface ActivityInfoSuppliers {
  id: number;
  supplierName: string;
  status: boolean;
}
export interface ActivityDateTimeMeridIems {
  id: string;
  name: string;
}
export interface TimeZonesProps {
  id: string;
  name: string;
}
export interface FieldActivityDateAndTimeParams {
  originalStartDateTime: string | null;
  startDateTime: string | null;
  originalEndDateTime: string | null;
  endDateTime: string | null;
  originalTimeZoneId: string;
  timeZoneId: string;
  originalAlternate2StartWhen: string | null;
  alternate2StartWhen: string | null;
  alternate2EndWhen: string | null;
  alternate2TimeZoneId: string;
  originalAlternate3StartWhen: string | null;
  alternate3StartWhen: string | null;
  alternate3EndWhen: string | null;
  alternate3TimeZoneId: string;
  pastStartDateTime: string | null;
  startTime?: string | null;
  endTime?: string | null;
  alternate2StartTime?: string | null;
  alternate2EndTime?: string | null;
  alternate3StartTime?: string | null;
  alternate3EndTime?: string | null;
}
export interface FieldActivityDateAndTimeControlParams {
  activityId: number;
  activityStatusId: number;
  activityDescriptionId: number;
  activitySupplierId: number;
  startDateTime: string | null;
  alternate2StartWhen: string | null;
  alternate3StartWhen: string | null;
  sbStatus: number;
  withinWindowPeriod: boolean;
  userTypeId: number;
  supplierId: number;
}

export interface FieldActivitySaveDateAndTimeParams {
  activityId: number;
  activityStatusId: number;
  sbStatus: number;
  isVMSActivity: boolean;
  isDateChanged: boolean;
  isFacultyChanged: boolean;
  isVMSActivityDateChanged: boolean;
  cancelledByWhomId: number;
  cancellationReasonId: number;
  isCopyActivity: boolean;
  activityAddedWhen: string | null;
  activityInformation: any;
  activityDateTime: FieldActivityDateAndTimeParams;
  representatives: FieldActivityRepresentativesList[];
  faculties: FieldActivityFacultyParams[];
  activityAudience: ActivityAudienceParams;
  speakerTravel: FieldActivitySpeakerTravel;
  activityComments: ActivityActivityComments[];
  autoCancelConfigurationId: number;
  userTypeId: number;
  profileId: number;
}
export interface FieldActivityDateTimeConflictActivities {
  facultyName: string;
  activityIdentity: string;
  startDateTime: string;
  endDateTime: string;
  timeZone: string;
}

export interface ActivityDetails {
  activityIdentity: string;
  speakerStatus: string;
  pid: string;
  isLocationStatusVisible: boolean;
  locationStatusText: string;
  dmApprovalStatus: string;
  activityTypeId: number;
  brandDiseaseStateId: number;
  activityDescriptionId: number;
  topicId: number;
  assignedToId: string;
  supplier: string;
  receivedHow: string;
  isCVentRegisteredMeeting: boolean;
  isReceivedInTime: boolean;
  dateReceived: string;
  businessJustificationIds: string;
  isNoPayments: boolean;
  isCertified: boolean;
  userDetails: string;
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
}

export interface ActivityControlProps {
  isCityEnabled: boolean;
  isClinicalConversationDescription: boolean;
  isClosestAirportEnabled: boolean;
  isDistanceEnabled: boolean;
  isItineraryColumnVisible: boolean;
  isLTSProfile: boolean;
  isNextButtonVisible: boolean;
  isPastDated: boolean;
  isStateEnabled: boolean;
  isSuggestedHotelEnabled: boolean;
  isTravelRequired: boolean;
  isVMSActivity: boolean;
  isVMSUser: boolean;
  pidFacultyIds: string;
  states: FieldActivityLocationStatesList[];
}

export interface PaymentRequestProps {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  isDownloadExpenseFormVisible: boolean;
  loadActivityURL: string;
  activityIdentity: string;
  activityTypeId: number;
  activityTypeHeaderLabel: string;
  activityType: string;
  activityDescriptionId: number;
  activityDescriptionHeaderLabel: string;
  activityDescription: string;
  isCityStateColumnVisible: boolean;
  cityState: string;
  isDateColumnVisible: boolean;
  activityStartDate: string;
  activityStatusId: number;
  activityStatus: string;
  isAttendanceUploadColumnVisible: boolean;
  isUploadHyperlinkVisible: boolean;
  signInSheetLabel: string;
  isSignInSheetCompleted: boolean;
  signInSheetFileName: string;
  isNewAudiencePageEnabled: boolean;
  isAutoSignInsheet: boolean;
  isSignInSheetTemplate: boolean;
  isAttendanceApprovalColumnVisible: boolean;
  isAttendanceApprovalEnabled: boolean;
  attendanceApproval: boolean;
  attendanceApprovedBy: string;
  attendanceApprovedWhen: string;
  isSendNowLaterRowVisible: boolean;
  isSendNowEnabled: boolean;
  isSendNow: boolean;
  isSendLaterEnabled: boolean;
  isSentLater: boolean;
  isSendLaterDateEnabled: boolean;
  sendLaterDate: string;
  isDescriptionOfServiceAlertApproved: boolean;
  isFacultySearchSectionVisible: boolean;
  isAttentionNoteVisible: boolean;
  isComplianceAcknowledgementFormVisible: boolean;
  complianceAcknowledgementFormLabel: string;
  isComplianceAcknowledgementFormNoteVisible: boolean;
  isComplianceFormButtonEnabled: boolean;
  isPaymentRequestGridEnabled: boolean;
  faculyNameHeaderLabel: string;
  isInvoiceNumberColumnVisible: boolean;
  isDateSubmittedColumnVisible: boolean;
  typeHeaderLabel: string;
  amountHeaderLabel: string;
  isInvitationAcceptedColumnVisible: boolean;
  isFacultyAttendedColumnVisible: boolean;
  isAttendanceColumnVisible: boolean;
  isPaymentAuthorizedColumnVisible: boolean;
  isClientApprovalColumnVisible: boolean;
  isApprovalsColumnVisible: boolean;
  isIncludeInBatchColumnVisible: boolean;
  paymentRequests: PaymentRequestFacultyList[];
  totalRecordsCount: number;
  isAutoLinkActivitiesEnable: boolean;
  isSubmitButtonEnabled: boolean;
  brandId: number;
  isRFSNotRequired: boolean;
  activityEndDate: string;
  isTrainingActivity: boolean;
  isBILillyActivity: boolean;
  isAdvisingActivity: boolean;
  ignoreActivityDuration: boolean;
  isGPPExceptionPopUpVisible: boolean;
  gppExceptionPopUpHeader: string;
  gppExceptionPopUpText: string;
  isModuleSupplier: boolean;
  isMarketingFDAActivity: boolean;
  is2009TrainingActivity: boolean;
  isCheckForBudgetExceededAlert: boolean;
  totalBudget: number;
  actualBudget: number;
  plannedBudget: number;
  budgetThresold: number;
  travelValidationRequired: boolean;
  withinWindowPeriod: boolean;
  isBudgetExceeded?: boolean;
  fmvCalculations?: FmvCalculations[];
  autoLinkCalculations?: AutoLinkCalculations[];
  linkedActivityDetailList?: LinkedActivityDetailsResultsParams[];
}

export interface PaymentRequestFacultyList {
  rowSpanId: number;
  aftId: number;
  facultyId: number;
  facultyName: string;
  facultyRole: string;
  facultyTypeDescription: string;
  promotionallyTrained: string;
  contractSubStatus: string;
  isHonororiumAlertPending: boolean;
  w9OnFileLabel: string;
  contractFiles: PaymentRequestContractFiles[];
  isVarianceHyperlinkVisible: boolean;
  varianceFileName: string;
  deviations: PaymentRequestDeviations[];
  contractSentDate: string;
  invoiceNumber: string;
  dateSubmitted: string;
  typeLabel: string;
  isTypeHyperlinked: boolean;
  isPaymentType: boolean;
  isRequiresTravelVisible: boolean;
  isRequiresTravelEnabled: boolean;
  requiresTravel: boolean | null;
  isMilesCalculatedLabelVisible: boolean;
  milesCalculatedLabel: string;
  isChangeDistanceHyperlinkVisible: boolean;
  isTotalAmountVisible: boolean;
  totalAmount: number;
  standardLabel: string;
  isAmountVisible: boolean;
  isAmountEnabled: boolean;
  amount: number;
  travelDistance: number;
  travelAmountLabel: string;
  isTravelAmountVisible: boolean;
  isTravelAmountEnabled: boolean;
  travelAmount: number;
  isChangeTravelAmountHyperlinkVisible: boolean;
  originalTravelAmount: number;
  reIssue: number;
  originalAmount: number;
  isUnReserveFundEnabled: boolean;
  isUnReserveFund: boolean | null;
  isInvitationAcceptedEnabled: boolean;
  isInvitationAccepted: boolean | null;
  invitationAcceptedWhen: string;
  isFacultyAttendedEnabled: boolean;
  isFacultyAttended: boolean | null;
  activityAttendedDate: string;
  isActualStartDateVisible: boolean;
  isActualStartDateEnabled: boolean;
  actualStartDate: string;
  attendance: string;
  isPaymentAuthorizedVisible: boolean;
  isPaymentAuthorizedEnabled: boolean;
  paymentAuthorizedList: PaymentRequestPaymentAuthorizedList[];
  paymentAuthorizedId: number;
  isClientApprovalButtonVisible: boolean;
  isClientApprovalButtonEnabled: boolean;
  isCSApproved: boolean | null;
  isCSApprovedDateVisible: boolean;
  csApprovedDate: string;
  clientApprovalText: string;
  approvals: string;
  isIncludeInBatchLabelVisible: boolean;
  includeInBatchLabel: string;
  isIncludeInBatchVisible: boolean;
  isIncludeInBatchEnabled: boolean;
  includeInBatch: boolean | null;
  checkType: number;
  afeId: number;
  facultyStatusId: number;
  participationRoleId: number;
  activityFacultyAttendanceTypeId: number;
  isPromotionallyTrained: boolean;
  honorariumTravelRulesStatusId: number;
  travelComment: string;
  travelDistanceFlag: number;
  activityDuration: number;
  prepTime: number;
  destinationOUS: number;
  originOUS: number;
  ousTime: number;
  isOUSTravelAllowanceNA: boolean;
  honorariumReason: string;
  isTravelAmountConfirmed: boolean;
  additionalTravelComments: string;
  previousPreferredAddress1: string;
  previousPreferredAddress2: string;
  currentPreferredAddress1: string;
  currentPreferredAddress2: string;
  originalRequiresTravel: boolean;
  originalOriginOUS: number;
  originalDestinationOUS: number;
  originalActivityDuration: number;
  originalPrepTime: number;
  originalOusTime: number;
  originalTravelDistance: number;
  originalIsUnReserveFund: boolean;
  originalIsInvitationAccepted: boolean;
  originalIsFacultyAttended: boolean;
  originalActualStartDate: string;
  originalIsCSApproved: boolean;
  originalIncludeInBatch: boolean;
  afiId: number;
  originalIsPromotionallyTrained: boolean;
  originalIsOUSTravelAllowanceNA: boolean;
  originalIsTravelAmountConfirmed: boolean;
  isManualHonorariumTravelUpdate: boolean;
  estimatedTravelAmount: number;
  linkedActivities: string;
  travelValidationRequired: boolean;
  facultyTypeId: number;
  facultyAddedWhen: string;
  checkNumber: string;
  canChangePaymentAmount: number;
  amountRevisedToZeroWhen: string;
  docOnFile: boolean;
  w9OnFile: boolean;
  contractName: string;
  csApprovalType: number;
  activityTopicId: number;
  requiresCSApproval: boolean;
  travelHonorarium: number;
  originalTravelDistanceFlag: number;
  originalAdditionalTravelComments: string;
  originalPreviousPreferredAddress1: string;
  originalPreviousPreferredAddress2: string;
  originalCurrentPreferredAddress1: string;
  originalCurrentPreferredAddress2: string;
  sentContractDetails: string;
  originalAFTId: number;
  modifiedTravelAmount?: number;
  rowId?: number;
  rowClassName?: number;
}
export interface PaymentRequestContractFiles {
  contractId: number;
  isContractNameHyperlinked: boolean;
  contractName: string;
  fileName: string;
}
export interface PaymentRequestDeviations {
  alertTypeId: number;
  deviationName: string;
  fileName: string;
}
export interface PaymentRequestPaymentAuthorizedList {
  id: number;
  name: string;
}

export interface SearchFacultyFormType {
  activityId: String;
  facultyId: number | null;
  facultyLastName: String;
  facultyFirstName: String;
  hideUnReservedFunds: boolean;
  pageIndex: number;
  pageSize: number;
  sortOrder: string;
}

export interface DownloadExpenseFormType {
  activityId: String;
  profileId: number;
  isBILillyActivity: boolean;
  isFromCheckStatus: boolean;
}

export interface WorkProductType {
  id: number;
  description: string;
  status: boolean;
}

export interface ApprovalStatusDetailType {
  approvalType: String;
  approvalStatus: String;
  approverName: String;
  approvedWhen: String;
  AlertTypeId: number;
  ApprovalStatusId: number;
  nmpCommunicationName: String;
}

export interface AdvisingActivityMasterGridType {
  gridTitle: string;
  mainHeader1: string;
  subHeaderC1: string;
  subHeaderC2: string;
  approverL1: string;
  approverL2: string;
  approverL3: string;
  lillyL1: string;
  lillyL2: string;
  lillyL3: string;
  userDetails: string;
}

export interface PaymentRequestSignInSheetFormType {
  activityId: String;
  isSignInSheetCompleted: boolean;
  signInSheetFileName: String;
  isNewAudiencePageEnabled: boolean;
  isAutoSignInsheet: boolean;
  isSignInSheetTemplate: boolean;
}

export interface AdvisingHonorariumParamsDialogFormType {
  activityId: number;
  aftId: number;
}
export interface AdvisingHonorariumDialogType {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  isActivityDurationHoursVisible: boolean;
  activityDurationHours: AdvisingHonorariumNameIdType[];
  activityDurationHourId: number;
  isActivityDurationVisible: boolean;
  isActivityDurationEnabled: boolean;
  activityDuration: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  };
  isChairPrepTimeVisible: boolean;
  isChairPrepTimeEnabled: boolean;
  chairPrepTime: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  };
  isModeratorPrepTimeVisible: boolean;
  isModeratorPrepTimeEnabled: boolean;
  moderatorPrepTime: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  };
  isPresenterPrepTimeVisible: boolean;
  isPresenterPrepTimeEnabled: boolean;
  presenterPrepTime: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  };
  isAttendeePrepTimeVisible: boolean;
  isAttendeePrepTimeEnabled: boolean;
  attendeePrepTime: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  };
  isOUSTravelTimeHoursVisible: boolean;
  ousTravelTimeHours: number;
  isOUSTravelVisible: boolean;
  ousTravel: {
    isOriginEnabled: boolean;
    origins: AdvisingHonorariumNameIdType[];
    originId: number;
    isDestinationEnabled: boolean;
    destinations: AdvisingHonorariumNameIdType[];
    destinationId: number;
    travelAllowance: number;
  };
  isOUSTravelAllowanceNotApplicableVisible: boolean;
  isOUSTravelAllowanceNotApplicableEnabled: boolean;
  isOUSTravelAllowanceNotApplicable: boolean;
  isOUSCheckboxTravelLabelVisible: boolean;
  ignoreActivityDuration: boolean;
  isActivityDurationInDays: boolean;
  participationRoleId: number;
  maxPerDayHours: number;
  activityStartDate: string;
  activityEndDate: string;
  honorariumReason?: string;
  isHonorariumReasonVisible?: boolean;
  isHonorariumChanged?: boolean;
}

export interface SaveAdvisingHonorariumDialogType {
  activityId: number;
  aftId: number;
  activityDurationHourId: number;
  activityDuration: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  } | null;
  chairPrepTime: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  } | null;
  moderatorPrepTime: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  } | null;
  presenterPrepTime: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  } | null;
  attendeePrepTime: {
    isDaysVisible: boolean;
    days: AdvisingHonorariumNameIdType[];
    dayId: number;
    hours: AdvisingHonorariumNameIdType[];
    hourId: number;
    minutes: AdvisingHonorariumNameIdType[];
    minuteId: number;
  } | null;
  ousTravelTimeHours: number;
  ousTravel: {
    isOriginEnabled: boolean;
    origins: AdvisingHonorariumNameIdType[];
    originId: number;
    isDestinationEnabled: boolean;
    destinations: AdvisingHonorariumNameIdType[];
    destinationId: number;
    travelAllowance: number;
  } | null;
  isOUSTravelAllowanceNotApplicable: boolean;
  honorariumReason?: string;
  isHonorariumChangedOkButttonClicked: boolean;
}

export interface AdvisingHonorariumNameIdType {
  id: number;
  name: string;
}

export interface AdvisingHonorariumSaveResponseParams {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  isHonorariumReasonVisible: boolean;
  isHonorariumChanged: boolean;
  activityDuration: number;
  prepTime: number;
  originOUS: number;
  destinationOUS: number;
  ousTime: number;
  amount: number;
  isOUSTravelAllowanceNA: boolean;
  honorariumReason: string;
}
export interface ComplianceAcknowledgementFormParams {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  faculties: ComplianceAcknowledgementFormFacultiesParams[];
}

export interface ComplianceAcknowledgementFormFacultiesParams {
  facultyId: number;
  facultyName: string;
  isComplianceOptionsEnabled: boolean;
  isCompliance: boolean;
  dateSentWhen: string;
  comments: string;
}

export interface ComplianceAcknowledgementSaveFormParams {
  activityId: number;
  profileId: number;
  switchedProfileId: number;
  faculties: ComplianceAcknowledgementFormFacultiesParams[];
}

export interface RelatedActivitiesParamsDialogFormType {
  activityId: number;
  aftId: number;
  travelDistance: number;
  amount: number;
  additionalTravelComments: string;
  isTravelAmountConfirmed: boolean;
  isManualHonorariumTravelUpdate: boolean;
  previousPreferredAddress1: string;
  previousPreferredAddress2: string;
  currentPreferredAddress1: string;
  currentPreferredAddress2: string;
  isFromChangeDistance: boolean;
  profileId: number;
  supplierId: number;
  switchedProfileId: number;
  linkedActivityList: PaymentRequestRelatedActivitiesList[];
  requiresTravel: boolean | null;
}
export interface RelatedActivitiesResponseToPaymentRequestProps {
  amount: number;
  travelDistance: number | null;
  additionalTravelComments: string | null;
  isTravelAmountConfirmed: boolean;
  previousPreferredAddress1: string | null;
  previousPreferredAddress2: string | null;
  currentPreferredAddress1: string | null;
  currentPreferredAddress2: string | null;
  fmvCalculations: FmvCalculations[];
  autoLinkCalculations: AutoLinkCalculations[];
  linkedActivityDetailList: LinkedActivityDetailsResultsParams[];
  travelAmount: number;
  requiresTravel: boolean | null;
  travelDistanceFlag: number | null;
  estimatedTravelAmount: number | null;
  linkedActivities: string | null;
  selectedLinkedActivity: PaymentRequestRelatedActivitiesList[];
}

export interface PaymentRequestRelatedActivities {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  isFeedBack: boolean;
  programType: string;
  isLTSSupplier: boolean;
  activityIdentity: string;
  contractFiles: PaymentRequestRelatedActivitiesContractFiles[];
  faculty: string;
  previousPreferredAddress1: string;
  previousPreferredAddress2: string;
  currentPreferredAddress1: string;
  currentPreferredAddress2: string;
  eventLocation: string;
  isActualStartDateVisible: boolean;
  actualStartDate: string;
  possibleRelatedActivities: PaymentRequestRelatedActivitiesList[];
  linkedActivityList: PaymentRequestRelatedActivitiesList[];
  additionalTravelComments: string;
  oneWayDistance: string;
  isMileageActualEnabled: boolean;
  isTravelAmountConfirmed: boolean;
  travelRules: PaymentRequestRelatedActivitiesTravelRules[];
  travelDistance: number;
  currentPreferredAddress: string;
}

export interface PaymentRequestRelatedActivitiesContractFiles {
  contractId: number;
  isContractNameHyperlinked: boolean;
  contractName: string;
  fileName: string;
}
export interface PaymentRequestRelatedActivitiesList {
  rowId: number;
  originalAftId: number;
  facultyId: number;
  relatedActivityId: number;
  status: string;
  startDateTime: string;
  endDateTime: string;
  actualStartDate: string;
  preferredLocation: string;
  eventLocation: string;
  speaker: string;
  activityDescription: string;
  isTravelRequired: boolean;
  travelRequired: string;
  estimatedTravelDistance: number;
  isManualHonorariumTravelUpdate: boolean;
  travelFacultyId: number;
}

export interface PaymentRequestRelatedActivitiesTravelRules {
  distanceStartRange: number;
  distanceEndRange: number;
}
export interface PaymentRequestRelatedActivitiesSearchActivity {
  activityIdentity: string;
  pidNumber: string;
  brandId: number;
  startDate: string;
  endDate: string;
  activityDescriptionId: number;
  stateId: number;
  supplierName: string;
  facultyLastName: string;
  facultyFirstName: string;
  facultyId: number;
  lillyPersonId: string;
  representativeLastName: string;
  representativeFirstName: string;
  city: string;
  countryId: number;
  scsMeetingId: string;
}
export interface PaymentRequestRelatedActivitiesBrand {
  id: number;
  brandName: string;
  status: boolean;
  supplierId: number;
}
export interface PaymentRequestRelatedActivitiesActivityDescriptions {
  id: number;
  name: string;
  activityTypeId: number;
  activitySubTypeId: number;
  status: boolean;
  ruleActivityTypeId: number;
  isTraining: boolean;
  brandIds: string;
  isARFActivityDescription: boolean;
  faTypeId: number;
  meetingTypeId: number;
  mappedCostElement: string;
  attendeeTypeIds: string;
  isARFParticipantRequired: boolean;
}
export interface PaymentRequestRelatedActivitiesCountry {
  id: number;
  name: string;
}

export interface PaymentRequestRelatedActivitiesState {
  id: number;
  name: string;
  countryId: number;
  shortName: string;
}

export interface LinkedActivityResultsParams {
  originalAftId: number;
  facultyId: number;
  relatedActivityId: number;
  status: string;
  startDateTime: string;
  endDateTime: string;
  actualStartDate: string;
  preferredLocation: string;
  eventLocation: string;
  speaker: string;
  activityDescription: string;
  travelRequired: string;
  estimatedTravelDistance: number;
  isManualHonorariumTravelUpdate: boolean;
  travelFacultyId: string;
  isFeedBack: number;
  programType: string;
  activityType: string;
  isFromAHMFeed: boolean;
  activityId: number;
}
export interface ChangeTravelAmountDialogParams {
  activityId: number;
  aftId: number;
  travelAmount: number;
  isAutoLinkActivitiesEnable: boolean;
}
export interface ChangeTravelAmountForm {
  newAmount: number | null;
  reason: string;
}
export interface SaveRelatedActivityParams {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  amount: number;
  travelDistance: number;
  additionalTravelComments: string;
  isTravelAmountConfirmed: boolean;
  previousPreferredAddress1: string;
  previousPreferredAddress2: string;
  currentPreferredAddress1: string;
  currentPreferredAddress2: string;
  fmvCalculations: FmvCalculations[];
  autoLinkCalculations: AutoLinkCalculations[];
  travelAmount: number;
  estimatedTravelAmount: number;
  linkedActivities: string;
  requiresTravel: boolean;
  travelDistanceFlag: number;
}
export interface RefreshRelatedActivityDataParams {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  oneWayDistance: number;
  googleCalculatedMilage: number | null;
  isDistanceRefreshed: boolean;
  previousPreferredAddress1: string;
  previousPreferredAddress2: string;
  currentPreferredAddress1: string;
  currentPreferredAddress2: string;
}

export interface FmvCalculations {
  aftId: number;
  facultyId: number;
  activityId: number;
  originalHonorariumAmount: number;
  originalTravelAmount: number;
  miles: number;
  isAllianceActivity: boolean;
  newHonorariumAmount: number;
  newTravelAmount: number;
  sourceActivityAddr: string;
  destinationActivityAddr: string;
  changedDueToActivity: number;
  isAllianceChangedDueToActivity: boolean;
}
export interface AutoLinkCalculations {
  mainAFTId: number;
  aftId: number;
  facultyId: number;
  activityId: number;
  originalHonorariumAmount: number;
  originalTravelAmount: number;
  originalTravelDistance: number;
  newTravelDistance: number;
  newTravelAmount: number;
  sourceActivityAddr: string;
}
export interface ModifiedIdsListType {
  aftId: number;
  facultyName: string;
  travelAmount: number;
  comment?: string;
}

export interface LinkedActivityDetailsResultsParams {
  aftId: number;
  facultyId: number;
  activityId: number;
  activityTypeKey: number;
  brandKey: number;
  activityDescriptionKey: number;
  supplierKey: number;
  activityStartDate: string;
  activityEndDate: string;
  noPayments: boolean;
  requiresCSApproval: boolean;
  reasonForRevHonorarium: string;
  checkType: number;
  isReissueCheckType: boolean;
  amountRevisedToZeroWhen: string;
  amount: number;
  estimatedTravelAmount: number;
  estimatedTravelDistance: number;
  isManualHonorariumTravelUpdate: boolean;
}

export interface SavePaymentRequestRequestParams {
  activityId: number;
  isAttendanceApprovalEnabled: boolean;
  attendanceApproval: boolean;
  isSendNowEnabled: boolean;
  isSendNow: boolean;
  isSendLaterEnabled: boolean;
  isSentLater: boolean;
  isSendLaterDateEnabled: boolean;
  sendLaterDate: string;
  paymentRequests: PaymentRequestFacultyList[];
  isBudgetExceeded: boolean;
  fmvCalculations: FmvCalculations[];
  autoLinkCalculations: AutoLinkCalculations[];
  linkedActivityDetailList: LinkedActivityDetailsResultsParams[];
  isCheckForBudgetExceededAlert: boolean;
  totalBudget: number;
  travelValidationRequired: boolean;
  withinWindowPeriod: boolean;
  groupId: number;
  profileId: number;
  switchedProfileId: number;
}
export interface PaymentRequestFileListProps {
  fileName: string;
  documentTypeId: number;
  alertTypeId: number;
  type: string;
}
export interface PaymentRequestFileProps {
  fileName: string;
  documentTypeId: number;
  alertTypeId: number;
}
export interface PaymentRequestContractListProps {
  contractId: number;
  fileName: string;
}
export interface PaymentRequestContractProps {
  activityId: number;
  contractId: number;
  fileName: string;
}
export interface PFFacultyList {
  facultyKey: number;
  lastName: string;
  firstName: string;
}
export interface FetchPaymentRequestComplianceFormProps {
  activityId: number;
  facultyId: number | null;
  facultyLastName: string;
  facultyFirstName: string;
  sortCol: string;
  sortOrder: string;
}

export interface CancelActivitiesCancelledByController {
  id: number;
  name: string;
}

export interface CancelActivitiesCancellationReasonsController {
  id: number;
  name: string;
  isCCRepresentative: number;
  isCCSpeaker: number;
  availableInCPS: number;
}
export interface SaveCancelActivitiesParams {
  activityId: number;
  cancelledByWhomId: number;
  cancellationReasonId: number;
  speakerNotifiedOfCancellation: boolean | null | undefined;
  dateSpeakerNotified: string | null;
  isEmailPresent: boolean | null | undefined;
  profileId: number;
  supplierId: number;
  switchedProfileId: number;
  startDateTime: string | null;
  endDateTime: string | null;
  paymentMode: boolean;
  activityStatusId: number;
  activityTypeId: number;
  brandId: number;
  activityAddedWhen: string | null;
  isLillyCloseOutActivity: boolean;
  travelValidationRequired: boolean;
  representatives: FieldActivityRepresentativesList[];
  faculties: FieldActivityFacultyParams[];
}

export interface DistributionsList {
  id: number;
  name: string;
}
export interface AdditionalPIDDistributionsList {
  rowId: number | string;
  name: string;
  division: string;
  area: string;
  district: string;
  territory: string;
  clientBusinessUnitGroupKey: string;
  clientBusinessUnitKey: string;
  clientBUDivisionKey: string;
  territoryId: string;
  divisionResultPage: string;
  areaResultPage: string;
  districtResultPage: string;
  territoryResultPage: string;
  fieldType?: string;
}
export interface SearchTerritoriesRequestParams {
  fieldType: number | undefined;
  fieldValue: string | undefined;
  quickSearchText: string | null;
  pageIndex: number;
  pageSize: number;
  sortCol: string;
  sortOrder: string;
}

export interface NewSelectedRepresentativeRowParams {
  individualKey: string;
  firstName: string;
  lastName: string;
  territoryId: string;
  territoryName: string;
  supplierName: string;
  area: string;
  costCenterKey: string;
  supplierShortName: string;
  email: string;
  bestContactNumber: string;
  isPRRepresentative: boolean;
  isCostCenterRuleApplied: boolean;
  ruleAppliedCostCenter: string;
  isRepBlocked: boolean;
  repSupplierId: number;
  repId: number;
  divisionId: string;
  hasCreditCard: boolean;
  ioNumber: string;
  addressPortablePhone: string;
  confirmedCellPhoneNo: string;
  faceTimeAccountId: string;
  originalCostCenter: string;
  originalIONumber: string;
}

export interface RepresentativeContactInfo {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  salutation: string;
  firstName: string;
  mi: string;
  lastName: string;
  suffix: string;
  bestContactNumber: string;
  telephoneNumber: string;
  mobileNumber: string;
  voiceMailNumber: string;
  faxNumber: string;
  email: string;
  address: string;
  faceTimeAccount: string;
}
export interface ActivityHeaderDetailParams {
  activityIdentity: string;
  speakerStatus: string;
  startDateTime: string;
  endDateTime: string;
  isApprovalStatusColumnVisible: boolean;
  isActivityStatusColumnVisible: boolean;
  activityStatus: string;
  isSupplierColumnVisible: boolean;
  supplier: string;
  isSpeakerStatusColumnVisible: boolean;
  speakerStatusColumnHeaderLabel: string;
  isLocationStatusColumnVisible: boolean;
  locationStatus: string;
  isStartDateTimeColumnVisible: boolean;
  isEndDateTimeColumnVisible: boolean;
}

export interface GetCancelActivityParams {
  activityId: number;
  activityDescriptionId: number;
  supplierId: number;
  userTypeId: number;
  cancelledByWhomId: number;
  cancellationReasonId: number;
}

export interface ActivitySeriesListParams {
  activityDate: string;
  bookedDate: string;
  activityId: number;
  activityIdentity: string;
}
export interface UserTrainingResponse {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  userTrainingDetails: {
    mostRecentCPSOverviewCompletedDate: string;
    mostRecentMarketingCompletedDate: string;
    mostRecentMedicalCompletedDate: string;
    mostRecentApproversCompletedDate: string;
    isTrainingCPSOverviewCompleted: boolean;
    isTrainingMarketingMedicalCompleted: boolean;
    isTrainingDiscoveryMedicalCompleted: boolean;
    isTrainingApproversCompleted: boolean;
    hideDiscoveryMedResearchPopup: boolean;
    hideMarketingMedicalPopup: boolean;
  };
  userTrainingCategories: UserTrainingCategoriesParams[];
}
export interface UserTrainingCategoriesParams {
  id: number;
  isSubCategory: boolean;
  displayText: string;
}
export interface CancelActivityParams {
  cancelledByWhomId: number;
  cancellationReasonId: number;
  speakerNotifiedOfCancellation: boolean;
  dateSpeakerNotified: string | null;
  startDateTime: string | null;
  paymentMode: boolean;
  activityStatusId: number;
  activityTypeId: number;
  brandId: number;
  activityAddedWhen: string | null;
  isLillyCloseOutActivity: boolean;
  travelValidationRequired: boolean;
}
export interface CancelActivitySetValuesParams {
  isEmailInvitationVisible: boolean;
  isEmailPresent: boolean | null | undefined;
  dateSpeakerNotified: string | null;
  isAutoCancel: boolean;
  cancelledByWhomId: number;
  cancellationReasonId: number;
}
export interface EPRIItemParams {
  id: number;
  description: string;
}
export interface SpecialtiesParams {
  id: number;
  name: string;
  status: number;
}
export interface CheckFacultyIdentificationRuleParams {
  activityTypeId: number;
  brandId: number;
  activityDescriptionId: number;
  supplierId: number;
  startDateTime: string | null;
  activityAddedWhen: string | null;
  groupId: number;
}
export interface CheckAudienceAndFacultyConsolidationParams {
  activityTypeId: number;
  brandId: number;
  activityDescriptionId: number;
  supplierId: number;
  activityAddedWhen: string | null;
}

export interface DateTimeLocalParams {
  isPastDateClickedYes: boolean;
  pastActivityStartDate: boolean;
  cancelledActivityDate: string | null;
  isAutoCancel: boolean;
  hiddenIsResheduled: boolean;
  isLillyCloseOutEnabled: boolean;
  lillyCloseOutStartDate: string | null;
  isLillyCloseOut: boolean;
  isFromCommentsPage: boolean;
  isDateTimeChangedProcessed?: boolean;
}
export interface FacultyLocalParams {
  isAudienceAndFacultyConsolidationEnabled: boolean;
  isHeaderVisible: boolean;
  isNavigationButtonVisible: boolean;
  isSpeakerIdentificationRuleApplicable: boolean;
  speakerIdentificationRuleId: number;
  isAllowExceptionApplicable: boolean;
  isLillyCloseOutEnabled: boolean;
  lillyCloseOutStartDate: string | null;
}

export interface AdditionalPIDDistributionsSearchedList {
  rowId: number | string;
  name: string;
  divisionId: string;
  division: string;
  divisionResultPage: string;
  areaId: string;
  area: string;
  areaResultPage: string;
  districtId: string;
  districtName: string;
  districtResultPage: string;
  territoryId: string;
  territoryName: string;
  territoryResultPage: string;
  isSelectEnabled: boolean;
  isSelect: boolean;
}

export interface RepresentativesLocalParams {
  originalLeadRepId: number;
  blockedLeadRepId: number;
  selectedLeadRepId: number;
  isRepCellPhoneCorrect: boolean;
  isCellPhoneSectionVisible: boolean;
  cellPhone: string;
  isRepCellPhoneCorrectEnabled: boolean;
  isFaceTimeSectionVisible: boolean;
  faceTime: string;
}

export interface FieldActivityRepresentativeControlParams {
  activityId: number;
  activityStatusId: number;
  activityDescriptionId: number;
  activitySupplierId: number;
  paymentMode: boolean;
  startDateTime: string | null;
  costElement: string;
  supplierId: number;
  roles: string;
  withinWindowPeriod: boolean;
}
export interface QRCodeRequestParams {
  activityId: number;
  activityIdentity: string;
  isFieldActivity: boolean;
  isFeedback: boolean;
  activityBrand: string;
  topic: string;
  startDateTime: string | null;
  endDateTime: string | null;
}
export interface QRCodeResponseParams {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  activityIdentity: string;
  brandLabel: string;
  activityBrand: string;
  topic: string;
  startEndDateLabel: string;
  productLogo: string;
  qrOutlineColor: string;
  qrFooterTextColor: string;
  qrFooterColor: string;
  qrCode: string;
}
export interface MatchActivityProps {
  activityId: number;
  startDateTime: string | null;
  facultyIds: string;
  pageIndex: number;
  pageSize: number;
}
export interface MatchActivityListProps {
  id: number;
  facultyId: number;
  facultyName: string;
  activityId: number;
  isActivityIdentityHyperlinked: boolean;
  activityIdentity: string;
  activityDescription: string;
  brand: string;
  startDateTime: string;
}
export interface ActivityLocationControlsParams {
  activityId: number;
  activityStatusId: number;
  activityTypeId: number;
  activityDescriptionId: number;
  activitySupplierId: number;
  brandId: number;
  representative: FieldActivityRepresentatives;
  travelValidationRequired: boolean;
  activityAddedWhen: string;
  isAutoMeetingLink: boolean;
  videoConferenceWebLink: string;
  startDateTime: string | null;
  withinWindowPeriod: boolean;
  supplierId: number;
}
export interface FacultySearchMyListParams {
  listId: number;
  listName: string;
  addedWhen: string;
  facultyCount: number;
  descriptions: string;
}
export interface FacultySearchStatesParams {
  id: number;
  name: string;
  countryId: number;
  shortName: string;
}
export interface FacultySearchCheckTypesParams {
  id: number;
  name: string;
}
export interface FieldActivityTravelControlParams {
  activityId: number;
  activityStatusId: number;
  activityDescriptionId: number;
  activitySupplierId: number;
  endDateTime: string | null;
  withinWindowPeriod: boolean;
  userTypeId: number;
  supplierId: number;
}
export interface FieldActivityProgramValidationControlPayloadParams {
  activityId: number;
  supplierId: number;
  brandId: number;
  activityDescriptionId: number;
  leadRepSupplierId: number;
  activitySupplierId: number;
  isActivityClosed: number;
  isProgramCoachingToolReviewed: boolean;
  programCoachingToolCheckboxAvailable: boolean;
  isCloseoutCertified: boolean;
  isNewLayout: boolean;
  llyReadOnlyUser: boolean;
  isMarketingActivityReadOnlyAccess: boolean;
  userTypeId: number;
  roles: string;
}
export interface FieldActivityProgramValidationControlParams {
  complianceText: string;
  isCloseoutCertified: boolean;
  isCompleteValidationButtonEnabled: boolean;
  isComplianceTextStarVisible: boolean;
  isComplianceTextVisible: boolean;
  isErrorMessage: boolean;
  isNewLayoutTextVisible: boolean;
  isProgramCoachingToolEnabled: boolean;
  isProgramCoachingToolVisible: boolean;
  isProgramValidationEnabled: boolean;
  isRequestorEnabled: boolean;
  isSeachRequestorHyperlinked: boolean;
  isSuccessMessage: boolean;
  isValidationMessage: boolean;
  message: string | null;
  messageCode: number;
  newLayoutText: string | null;
  programCoachingToolText: string;
  programValidationText: string;
  requestors: DropdownList[];
  complianceQuestionsWithAnswers?: ComplianceQuestionsWithAnswersParams[];
  faculties?: FieldActivityFacultyParams[];
  isCompleteValidationButtonVisible?: boolean;
  isNewLayout?: boolean;
  isSaveAndContinueButtonEnabled?: boolean;
  isSaveAndContinueButtonVisible: boolean;
  isSaveButtonEnabled?: boolean;
  isSaveButtonVisible?: boolean;
  requestorId?: number;
  requestorName?: string | null;
  activityDescriptionId?: number;
}

export interface SaveActivityLocationDetailsParams {
  activityId: number;
  activityTypeId: number;
  activityDescriptionId: number;
  supplierId: number;
  brandId: number;
  startDateTime: string | null;
  endDateTime: string | null;
  isNoPayments: boolean;
  activityLocation: FieldActivityActivityLocation;
  representative: FieldActivityRepresentatives;
  faculties: FieldActivityFacultyParams[];
  travelValidationRequired: boolean;
  profileId: number;
  switchedProfileId: number;
}

export interface EditLinksParams {
  name: string;
  link: string;
  isEnable: boolean;
  isHide?: boolean;
  handleFunction: any;
}

export interface FacultyIdNameParams {
  id: number;
  name: string;
}
export interface FacultyDetailsParams {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  salutationId: string;
  firstName: string;
  mi: string;
  lastName: string;
  suffix: string;
  credentials: string;
  telephoneNumber: string;
  telephoneNumberExtension: string;
  mobileNumber: string;
  faxNumber: string;
  email: string;
  address: string;
  isAssistantNameVisible: boolean;
  assistantName: string;
  isAssistantTelephoneNumberVisible: boolean;
  assistantTelephoneNumber: string;
  isAssistantEmailVisible: boolean;
  assistantEmail: string;
  faceTimeAccount: string;
}

export interface SearchActivityFacultyDataType {
  brandActivityTypeId: number;
  activityTypeId: number;
  activityDescriptionId: number;
  brandId: number;
  startDateTime: string;
  endDateTime: string;
  activitySupplierId: number;
  activityAddedWhen: string;
  locationCity: string;
  locationStateId: number | null;
  isFeedback: boolean;
  isActivityClosed: number;
  isSBSearch: boolean;
  isSpeakerIdentificationFlow: boolean;
  isAllowExceptionApplicable: boolean;
  reIssueCheckTypeId: number;
  searchFacultyTypeId: number;
  territoryId: string | null;
  leadRepId: number | null;
  advisingTypeId: number | null;
  supplierId: number;
  topicId: number | null;
  alternate2StartWhen: string | null;
  alternate3StartWhen: string | null;
  timeZone: string;
  checkTypeId: number;
  destinationId: number | null;
  oUSTravelTime: string | null;
  isSpeakerIdentificationRuleApplicable: boolean;
  speakerIdentificationRuleId: number;
  fATypeId: number;
  fAOver20AlertStatusId: number;
  sbStatus: number;
  faculties: FieldActivityFacultyParams[];
  cancellationReasonId: number;
  attendeeInformations: any;
  isFacultyChanged: boolean;
  isVMSActivityFacultyChanged: boolean;
  isDateChanged: boolean;
  travelValidationRequired: boolean;
  locationAddress1: string;
  locationAddress2: string;
  locationZipCode: string | null;
  isLocationGoogleVerified: boolean;
  isNoPayments: boolean;
  salesForceRuleStatus: number;
  activityInvitees: ActivityAudienceActivityInvitees[];
  facultyOrganizationalContactInfos: FacultyOrganizationalContactInfosParams[];
  representative: FieldActivityRepresentatives | null;
  addedByProfileId: number;
  lillyActivityOwnerEmailId: string | null;
  cVentMeetingId: string | null;
  mpcSupplierId: number;
  advisoryBoardsFacultyCount?: number;
}

export interface AddInviteePopUpDataType {
  isFieldActivity: any;
  activityId: number;
  activityDescriptionId: number;
  activityType: number;
  activityTypeId: number;
  activityAddedWhen: string;
  activityUpdatedWhen: string | null;
  brandId: number;
  topicId: number;
  startDateTime: string | null;
  endDateTime: string | null;
  locationCity: string;
  locationStateId: number;
  locationZipCode: string | null;
  locationAddress1: string;
  locationAddress2: string;
  isNonRSVPEmailEnabled: boolean;
  faculties: FieldActivityFacultyParams[];
  representatives: FieldActivityRepresentativesList[] | null;
  currentRSVPRegistrantId: number | null;
  isRSVPReconcileMode: boolean;
  isReconcileAgainMode: boolean;
  activityInvitees: ActivityAudienceActivityInvitees[];
  audienceSpecialities: ActivityAudienceSpecialities | null | {};
  activityRSVPInfos: ActivityRSVPInfosListParams[];
  writeInList: any[];
  isFeedback: boolean;
  occurredWhen: string | null;
  supplierId: number;
}

export interface ActivityTerritorySearchRequestType {
  activityId: number;
  inviteeTypeId: number;
  territoryId: string;
  searchType: number;
  pageIndex: number;
  sideBar: string;
  firstNameLastName: string;
  isPrimarySearch: boolean;
  hcoId: string;
  isAllSegmentCheckBoxChecked: boolean;
  isLowSegmentCheckBoxChecked: boolean;
  isMediumSegmentCheckBoxChecked: boolean;
  isHighSegmentCheckBoxChecked: boolean;
  isAllOpportunityPercentSegmentCheckBoxChecked: boolean;
  isTop10OpportunityPercentSegmentCheckBoxChecked: boolean;
  isTop25OpportunityPercentSegmentCheckBoxChecked: boolean;
  isTop50OpportunityPercentSegmentCheckBoxChecked: boolean;
  isBottom50OpportunityPercentSegmentCheckBoxChecked: boolean;
  activityDescriptionId: number;
  brandId: number;
  topicId: number;
  startDateTime: string;
  endDateTime: string;
  locationCity: string;
  locationStateId: number;
  locationZipCode: string | null;
  locationAddress1: string;
  locationAddress2: string;
  faculties: FieldActivityFacultyParams[];
  representatives: FieldActivityRepresentativesList[];
  activityInvitees: ActivityAudienceActivityInvitees[];
}

export interface ActivityFullSearchRequestType {
  activityId: number;
  lastName: string;
  firstName: string;
  city: string;
  stateId: number;
  zipCode: string;
  credentialId: number;
  specialtyId: string | null;
  licenseStateId: number;
  licenseNumber: string | null;
  npiNumber: string;
  activityDescriptionId: number;
  territoryId: string;
  pageIndex: number;
  pageSize: number;
  sideBar: any;
  hcoId: string;
  radiusId: number;
  isAllSegmentCheckBoxChecked: boolean;
  isLowSegmentCheckBoxChecked: boolean;
  isMediumSegmentCheckBoxChecked: boolean;
  isHighSegmentCheckBoxChecked: boolean;
  brandId: number;
  topicId: number;
  startDateTime: string;
  endDateTime: string;
  faculties: FieldActivityFacultyParams[];
  representatives: FieldActivityRepresentativesList[];
  activityInvitees: ActivityAudienceActivityInvitees[];
  isRSVPReconcileMode: boolean;
  isAllOpportunityPercentSegmentCheckBoxChecked: boolean;
  isTop10OpportunityPercentSegmentCheckBoxChecked: boolean;
  isTop25OpportunityPercentSegmentCheckBoxChecked: boolean;
  isTop50OpportunityPercentSegmentCheckBoxChecked: boolean;
  isBottom50OpportunityPercentSegmentCheckBoxChecked: boolean;
}

export interface TerritoryInviteeType {
  id: number;
  name: string;
  sortOrderId: number;
  isVisibleForDDL: boolean;
}

export interface TerritoryType {
  territoryId: string;
  territoryName: string;
  divisionName: string;
  areaName: string;
  districtName: string;
}

export interface TerritoryHcoType {
  hcoId: string;
  hcoName: string;
  address: string;
}

export interface TerritoryControlsApiResponse {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  isIntelligentSpeakerEffectiveDate: boolean;
  isNonRSVPEmailEnabled: boolean;
  isFDAActivity: boolean;
  inviteeTypes: TerritoryInviteeType[];
  territories: TerritoryType[];
  hcOs: TerritoryHcoType[];
  pageSize: number;
}
export interface FullSearchControlsResponseType {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  isIntelligentSpeakerEffectiveDate: boolean;
  isNonRSVPEmailEnabled: boolean;
  isFDAActivity: boolean;
  credentials: DropdownList[];
  specialtyList: SpecialtiesProps[];
  radiusList: DropdownList[];
  states: StatesProps[];
  hcOs: TerritoryHcoType[];
  pageSize: number;
}
export interface WriteInControlsResponseType {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isPhoneMandatory: boolean;
  isValidationMessage: boolean;
  positionStates: string;
  credentials: WriteInCredentialType[];
  specialtyList: WriteInSpecialtiesProps[];
  positions: DropdownList[];
  states: StatesProps[];
  writeInInvitee: any;
}

export interface WriteInCredentialType {
  id: string;
  name: string;
  detail: string;
  status: number;
}
export interface WriteInSpecialtiesProps {
  id: number;
  name: string;
  status: number;
}
export interface AddFacultyDataType {
  attendeeInformations: FieldActivityAttendeeInformations[];
  faculties: FieldActivityFacultyParams[];
  facultyOrganizationalContactInfos: FacultyOrganizationalContactInfosParams[];
  isErrorMessage: boolean;
  isFacultyChanged: boolean;
  isSuccessMessage: boolean;
  isVMSActivityFacultyChanged: boolean;
  isValidationMessage: boolean;
  message: string;
  messageCode: number;
}

export interface ConfirmFacultyReqParams {
  selectedFacultyId: number;
  activityDescriptionId: number;
  alternate2StartWhen: string | null;
  faculties: FieldActivityFacultyParams[];
  isConfirmedFromActions: boolean;
}

export interface RetainActivityInformationParams {
  activityId: number;
  startDateTime: string | null;
  supplierId: number;
  activityDescriptionId: number;
  managementApprovalReceivedWhen: string | null;
  dmApprovalStatus: string;
  currentAcceptedInvitees: number | null;
  representative: FieldActivityRepresentatives | null;
  faculties: FieldActivityFacultyParams[];
  activityAudience: ActivityAudienceParams;
  activityComments: ActivityActivityComments[];
  speakerTravel: FieldActivitySpeakerTravel;
  isFacultyChanged: boolean;
  cancelledByWhomId: number;
  cancellationReasonId: number;
  profileId: number;
  switchedProfileId: number;
}
export interface SaveFacultyParams {
  activityId: number;
  activityDescriptionId: number;
  brandId: number;
  supplierId: number;
  isDateChanged: boolean;
  startDateTime: string | null;
  isFacultyChanged: boolean;
  representatives: FieldActivityRepresentativesList[] | null;
  faculties: FieldActivityFacultyParams[];
  activityTravelBreadcrumbTypeStatusId: number;
  isBackButtonClicked: boolean;
  activityAddedWhen: string;
}
export interface ActivityChangeOVParams {
  activityId: number;
  selectedFacultyId: number;
  selectedOVId: number;
  faculties: FieldActivityFacultyParams[];
}

export interface DocumentTypesIdNameParams {
  id: number;
  name: string;
}
export interface SaveActivityReceiptsParams {
  activityId: number | undefined;
  documentTypeId: string;
  file: string;
  fileName: string;
  profileId: number;
}
export interface ReceiptsListParams {
  id: number;
  rowId: number;
  activityId: number;
  fileName: string;
  receiptTypeId: number;
  receiptStatusId: number;
  comments: string;
  displayName: string;
  status: boolean;
  addedBy: number;
  addedWhen: string;
  updatedBy: number;
  updatedWhen: string;
  amount: number;
  isGratuityPaid: boolean;
  gratuityAmount: number;
  isGratuityIncludedInAmount: boolean;
  totalAmount: number;
  thumbnailFileName: string;
  isAmountAttested: boolean;
  documentType: string;
  addedByName: string;
  receiptStatus: string;
}

export interface DeleteActivityReceiptsParams {
  activityId: number | undefined;
  activityReceiptId: number;
  profileId: number;
}
export interface DeleteActivitySISParams {
  sisId: number;
  profileId: number;
}
export interface GetReceiptBreakdownDetailsParams {
  activityId: number | undefined;
  activityReceiptId: number;
}

export interface FieldActivityInviteeTypesListParams {
  id: number;
  name: string;
  sortOrderId: number;
  isVisibleForDDL: boolean;
}
export interface FieldActivityListNameIdControllerParams {
  id: number;
  name: string;
  status: number;
}
export interface RSVPRegistrantDeleteParams {
  rsvpRegistrantId: number;
  profileId: number;
}
export interface SaveAndRefreshInviteeParams {
  activityId: number;
  activityDescriptionId: number;
  isFeedback: boolean;
  activityUpdatedWhen: string | null;
  activityInvitees: ActivityAudienceActivityInvitees[];
  profileId: number;
}

export interface UploadInviteeParams {
  activityId: number;
  isFeedback: boolean;
  activityUpdatedWhen: string | null;
  file: string;
  fileName: string;
  activityInvitees: ActivityAudienceActivityInvitees[];
  profileId: number;
}
export interface GetInviteeSignatureDetailsParams {
  activityInviteeId: number;
  activityDescriptionId: number;
}
export interface InviteeSignatureParams {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  inviteeTypeId: number;
  salutation: string;
  firstName: string;
  middleName: string;
  lastName: string;
  specialty: string;
  credential: string;
  isWriteIn: boolean;
  signature: string;
  signedDate: string;
  referenceId: number;
  lpid: string;
  isMealConsumed: boolean;
  isMealConsumedYes: string;
  isMealConsumedNo: string;
  isLegalLanguage: boolean;
  isSignaturePadVisible: boolean;
}
export interface SaveInviteeSignatureDetailsParams {
  activityInviteeId: number;
  signature: string;
  isMealConsumed: boolean | null;
  isAttended: boolean | null;
  profileId: number;
}

export interface SaveUploadSignInSheet {
  activityId: number;
  file: string;
  fileName: string;
  userName: string;
  profileId: number;
}
export interface UpdatedSignatureValuesParams {
  isAttended: boolean;
  isMealConsumed: boolean;
  isSigned: boolean;
  isUpdated: boolean;
  isPaperSignaturePresent: boolean;
  isSignatureDeclined: boolean;
}
export interface SaveInviteeEmailParams {
  activityInviteeId: number | undefined;
  email: string | undefined;
  profileId: number;
}

export interface GenerateActivitySISParams {
  activityId: number;
  supplierId: number;
  activityDescriptionId: number;
  profileId: number;
}

export interface FetchOrderMaterialDetailsParams {
  startDateTime: string | null;
  topicMMNItemAlias: string;
  leadRepId: number | undefined;
}
export interface InventoryListParams {
  itemId: number;
  itemCode: string;
  longDescription: string;
  itemAlias: string;
  quantity: number;
}
export interface MaterialOrderDetailsParams {
  orderId: number;
  orderIdentity: string;
  activityId: number;
  topicId: number;
  mmNumberItemAlias: string;
  itemId: number;
  itemCode: string;
  quantity: number;
  itemLongDescription: string;
  shippingMethodCode: number;
  shippingMethodName: string;
  firstName: string;
  middleInitial: string;
  lastName: string;
  shippingAddress1: string;
  shippingAddress2: string;
  shippingCity: string;
  shippingStateCode: string;
  shippingZip: string;
  trackingNumbers: string;
  orderCreateOn: string;
  orderStatusId: number;
  orderStatus: string;
  submittedOn: string;
  orderNumber: string;
  statusType: string;
  email: string;
  phone: string;
  leadRepId: number;
  overrideDefaultRepAddress: boolean;
}
export interface SaveMaterialOrderDetailsParams {
  activityId: number;
  activityIdentity?: number;
  topicId: number;
  leadRepId: number | undefined;
  firstName: string;
  middleInitial: string;
  lastName: string;
  shippingAddress1: string;
  shippingAddress2: string;
  shippingCity: string;
  shippingState: number;
  shippingZip: string;
  email: string;
  phone: string;
  inventoryList: InventoryListParams[];
  shippingMethodId: number;
  overrideDefaultRepAddress: true;
  savedOrdersCount: number;
  materialOrderDetails: MaterialOrderDetailsParams[];
  profileId: number;
  isNewActivity: boolean;
  hasFacultyConfirmedJustNow: boolean;
}
export interface DeleteMaterialOrderDetailsParams {
  activityId: number;
  topicId: number;
  materialOrderDetails: MaterialOrderDetailsParams[];
  profileId: number;
}
export interface SaveNewAudienceParams {
  activityId: number;
  activityDescriptionId: number;
  activityTypeId: number;
  brandId: number;
  supplierId: number;
  isFeedback: boolean;
  startDateTime: string | null;
  activityUpdatedWhen: string | null;
  isAudienceAndFacultyConsolidationEnabled: boolean;
  isFieldActivity: boolean;
  territoryId: string;
  faculties: FieldActivityFacultyParams[];
  activityTravelBreadcrumbTypeStatusId: number;
  isBackButtonClicked: boolean;
}

export interface GetProgramCloseOutDetailsParams {
  activityId: number;
  breadcrumbId: number;
  profileId: number;
}

export interface MeetingMaterialsParams {
  meetingMaterialId: number;
  meetingMaterialTypeId: number;
  fileName: string;
  originalFileName: string;
  notes: string;
  addedBy: string;
  addedWhen: string;
  meetingMaterialTypes: FieldActivityListNameIdControllerParams[];
}
export interface SaveMeetingMaterialsParams {
  meetingMaterials: MeetingMaterialsParams[];
  activityId: number;
  profileId: number;
  switchedProfileId: number;
}
export interface DeleteMeetingMaterialParams {
  meetingMaterialId: number;
  activityId: number;
  profileId: number;
  switchedProfileId: number;
}
export interface UploadMeetingMaterialParams {
  activityId: number;
  meetingMaterialTypeId: number;
  fileName: string;
  file: string;
  profileId: number;
  switchedProfileId: number;
}
export interface ActivitySummaryDocumentsListParams {
  filePath: string;
  displayName: string;
  addedWhen: string;
}
export interface FetchActivitySummaryDocumentsParams {
  filePath: string;
  displayName: string;
}
export interface FetchActivityCommentsParams {
  activityId: number;
  activityTypeId: number;
  activitySupplierId: number;
  activityStatusId: number;
  withinWindowPeriod: boolean;
  originalActivityDescriptionId: number;
  activityDescriptionId: number;
  originalStartDateTime: string | null;
  startDateTime: string | null;
  originalEndDateTime: string | null;
  endDateTime: string | null;
  originalDescriptionOfService: string;
  descriptionOfService: string;
  isDateChanged: boolean;
  isFacultyChanged: boolean;
  isVMSActivityDateChanged: boolean;
  isVMSActivityFacultyChanged: boolean;
  sbStatus: number;
  cancellationReasonId: number;
  activityLocation: FieldActivityActivityLocation;
  faculties: FieldActivityFacultyParams[];
  representatives: FieldActivityRepresentativesList[];
  activityAudience: ActivityAudienceParams;
  activityComments: FieldActivityCommentsListParams[];
  activityAddedWhen: string;
  planHonorariumTotal: number | null;
  planExpenseTotal: number | null;
  originalTotalPlannedAmount: number;
  totalPlannedAmount: number;
  totalActualAmount: number;
  addedBySupplierId: number;
  isLillyCloseOutEnabled: boolean;
  mpcSupplierId: number;
  supplierId: number;
  userTypeId: number;
  userName: string;
  llyReadOnlyUser: boolean;
  isMarketingActivityReadOnlyAccess: boolean;
}
export interface FieldActivityCommentsListParams {
  id: number;
  supplier: string;
  addedBy: string;
  addedWhen: string;
  comments: string;
  commentType: string;
  commentTypeId: number;
  isDeleteButtonVisible: boolean;
}
export interface SaveFieldActivityCommentsListParams {
  activityId: number;
  id: number;
  commentTypeId: number;
  activityComment: string;
  isDeleted: boolean;
  profileId: number;
  switchedProfileId: number;
}

export interface FetchFieldActivityInformationDetailsParams {
  activityId: number;
  supplierId: number;
  userTypeId: number;
  llyReadOnlyUser: boolean;
  isMarketingActivityReadOnlyAccess: boolean;
  roles: string;
}
export interface FetchPartnerRepresentativesParams {
  activityTypeId: number;
  brandId: number;
  supplierId: number;
  activityDescriptionId: number;
  startDateTime: string | null;
  locationStateId: number;
  repId: number;
  representatives: FieldActivityRepresentativesList[];
}

export interface FetchFinancialValidationDetailsParams {
  activityId: number;
  profileId: number;
}

export interface ActivityReceiptsListParams {
  activityReceiptId: number;
  activityReceiptImage: string;
  receiptName: string;
  amount: number;
}
export interface ActivityFinancialsListParams {
  tovSecondaryCategoryId: number;
  tovSecondaryCategoryName: string;
  actualAmount: number | string;
  isCostPerPersonApplicable: boolean;
}
export interface CanNavigatetoCloseOutParams {
  menuId: number;
  activityDescriptionId: number;
  activityStatusId: number;
  isActivityClosed: number;
  startDateTime: string | null;
  sbStatus: number;
  isProgramCloseOutEnabledForCancelledFieldFDA: boolean;
}
export interface SaveFinancialValidationDetailsParams {
  activityId: number;
  activityDescriptionId: number;
  activityFinancialValidationId: number;
  isMealPurchased: boolean | null;
  actualMealPurchased: number | null;
  isCostPerPersonOver: boolean | null;
  activityUpdatedWhen: string | null;
  isCompleteValidationButtonClicked: boolean;
  activityFinancials: ActivityFinancialsListParams[];
  meetingMaterials: MeetingMaterialsParams[];
  profileId: number;
  switchedProfileId: number;
}
export interface CheckLillyCloseOutEnabledParams {
  activityId: number;
  activityTypeId: number;
  activityStatusId: number;
  activityDescriptionId: number;
  brandId: number;
  supplierId: number;
  repSupplierId: number;
  isLillyCloseOutActivity: number;
  isDateChanged: boolean;
  isFacultyChanged: boolean;
}

export interface BusinessJustificationControllerParams {
  activityId: number;
  activityStatusId: number;
  isDateChanged: boolean;
  isFacultyChanged: boolean;
  activityDescriptionId: number;
  brandId: number;
  supplierId: number;
  topicId: number;
  autoCancelConfigurationId: number;
  isTopicEnabled: boolean;
  isDMApprovalStatusVisible: boolean;
  dmApprovalStatus: string;
  isCurrentAcceptedInviteesVisible: boolean;
  currentAcceptedInvitees: number;
  isManagementApprovalReceivedVisible: boolean;
  isManagementApprovalReceivedEnabled: boolean;
  isManagementApprovalReceived: boolean;
  managementApprovalReceivedWhen: string;
  isBusinessJustificationsVisible: boolean;
  businessJustifications: ActivityInfoBusinessJustifications[];
  activityAddedWhen: string;
  roles: string;
}
export interface ActivityMeetingInfoParams {
  id: number;
  meetingId: string;
  foodAndBeverageCost: string;
  hotelCost: string;
  transportationCost: string;
  totalAmount: string;
  epriCost: string;
  totalAttendees: string;
  userDetails: string;
}
export interface GetActivitySponsorContactsApiParams {
  activitySupplierId: number;
  activityDescriptionId: number;
  activityStatusId: number;
  activityAddedWhen: string | null;
  sponsorEmail1: string;
  sponsorEmail2: string;
  sponsorEmail3: string;
  supplierId: number;
}
export interface SponsorContactsListParams {
  id: string;
  name: string;
  profileAndTrainingStatus: string;
}

export interface IndividualPaymentsLimitsParams {
  product: string;
  commercialDecisionStatus: string;
  activityType: string;
  usedCount: number;
  availableCount: number;
}

export interface ProductLimitsParams {
  product: number;
  productName: string;
  activityTypeDescription: string;
  usedCount: number;
  availableCount: number;
}

export interface ActivitySponsorContactMasterGridDetails {
  cell1: string;
  cell2: string;
}
export interface ActivitySponsorContactMasterGrid {
  gridTitle: string;
  mainHeader1: string;
  cellHeader1: string;
  cellHeader2: string;
  gridDetails: ActivitySponsorContactMasterGridDetails[];
  userDetails: string;
}

export interface SelectedApproverInActivitySponsor {
  nameWithTitle: string;
  email: string;
  employeeId: number;
  profileAndTrainingStatus: string;
}

export interface LocationInformationParams {
  isLocationGoogleVerified: boolean;
  locationName: string | null;
  originalLocationName: string | null;
  locationContactName: string | null;
  originalLocationContactName: string | null;
  locationAddress1: string | null;
  originalLocationAddress1: string | null;
  locationAddress2: string | null;
  originalLocationAddress2: string | null;
  locationCity: string | null;
  originalLocationCity: string | null;
  locationStateId: number;
  originalLocationStateId: number;
  locationZipCode: string | null;
  originalLocationZipCode: string | null;
  locationCountryId: number;
  originalLocationCountryId: number;
  isAccomodationSame: boolean | null;
  accomodationLocationName: string | null;
  originalAccomodationLocationName: string | null;
  accomodationContactName: string | null;
  originalAccomodationContactName: string | null;
  accomodationAddress1: string | null;
  originalAccomodationAddress1: string | null;
  accomodationAddress2: string | null;
  originalAccomodationAddress2: string | null;
  accomodationCity: string | null;
  originalAccomodationCity: string | null;
  accomodationStateId: number;
  originalAccomodationStateId: number;
  accomodationZipCode: string | null;
  originalAccomodationZipCode: string | null;
  accomodationCountryId: number;
  originalAccomodationCountryId: number;
  isVirtualLocation?: boolean;
}

export interface FeedbackActivityAlertInfosParams {
  activityKey: number;
  alertType: number;
  approverName: string | null;
  approvalStatus: string;
  approvedWhen: string | null;
  addedBy: number;
  approvalStatusKey: number;
  approverLevel: number;
  isFeedbackBudgetActivity: number;
  isReApproval: boolean;
  alertKey: number;
}

export interface FeedBackActivityParams {
  activityUpdatedWhen: string | null;
  brandActivityTypeId: number;
  isDraftActivity: boolean;
  isARFActivity: boolean;
  feedbackActivityAlertInfos: FeedbackActivityAlertInfosParams[];
  feedbackActivityInformation: FeedBackActivityInformationParams;
  locationInformation: LocationInformationParams;
  originalParticipantsInformation: FeedBackParticipantsInformationParams;
  participantsInformation: FeedBackParticipantsInformationParams;
  advisoryDescription: FeedBackAdvisoryDescriptionParams;
  activityAudience: FeedBackActivityAudienceParams;
  materialOrderDetailsList: FeedBackMaterialOrderDetailsListParams;
  activityComments: FeedBackActivityComments[];
  activityStatusId: number;
  isActivityClosed: boolean;
  isBudgetExempted: boolean;
  draftBreadCrumbMessage: string;
  activityAddedWhen: string | null;
  isActivityCreatedAfterTLCOEStreamliningRelease: boolean;
  isCapPaymentEligible: boolean;
  isCapPaymentProductionDate: boolean;
  medicalApproversList: string | null;
  payOnSiteDescriptions: string;
  individualPaymentLimitDate: string;
  rfsContractForActivity: string | null;
  commercialDecisionStatusId: number;
  isPendingAlerts: boolean;
  biLillyBrandIds: string;
  maxPastDays: number;
  proactiveBookingProductionDate: string;
  isARFParticipantsPresent: boolean;
  travelValidationRequired: boolean;
  salesForceRuleStatus: number;
  isLillyCloseOutActivity: number;
  approvalStatusId: number;
  masterGridId: number;
  isLocationInfoSubmitted: boolean;
  isParticipantInfoSubmitted: boolean;
  isAdvisoryInfoSubmitted: boolean;
  addedByProfileId: number;
  originalActivityStatusId: number;
  csLevel1ApprovalStatus: number;
  originalCSLevel1ApprovalStatus: number;
  csLevel2ApprovalStatus: number;
  originalCSLevel2ApprovalStatus: number;
  csLevel3ApprovalStatus: number;
  originalCSLevel3ApprovalStatus: number;
  isFacultyCountAlert: boolean | null;
  originalIsFacultyCountAlert: boolean | null;
  originalActualAmount: number;
  rfsTemplateId: number;
  nonHCPTemplateId: number;
  patientTemplateId: number;
  commentTypeId: number;
  comment: string | null;
  budgetThreshold: number;
  isProactiveTravelBookingPopUp: boolean;
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
}

export interface FeedBackActivityComments {
  id: number;
  supplier: string | null;
  addedBy: string | null;
  addedWhen: string | null;
  comments: string | null;
  commentTypeId: number;
  commentType: number;
  isDeleteButtonVisible: boolean;
  status: number;
}

export interface FeedBackActivityInformationParams {
  isGrantSupplierAccessHyperlinkVisible: boolean;
  isHCPRegistrationAdminHyperlinkVisible: boolean;
  activityIdentity: string;
  isActivityStatusLabelVisible: boolean;
  activityStatusLabel: string;
  isOccurredVisible: boolean;
  isOccurredCheckboxEnabled: boolean;
  isOccurred: boolean;
  occurredDate: string;
  isCancelledVisible: boolean;
  isCancelledCheckboxEnabled: boolean;
  isCancelled: boolean;
  cancelledDate: string;
  isInactiveOptionVisible: boolean;
  isInactiveCheckboxEnabled: boolean;
  isInactive: boolean;
  arfId: number | string | null;
  attendeeTypes: DropdownWithStatusType[];
  attendeeTypeIds: string | null;
  originalAttendeeTypeIds: string | null;
  tlMapCAId: string | null;
  originalTLMapCAId: string | null;
  meetingTypes: DropdownWithStatusType[];
  meetingTypeIds: string | null;
  originalMeetingTypeIds: string | null;
  planSteward: string;
  originalPlanSteward: string;
  sponsoringOrganizations: DropdownWithStatusType[];
  sponsoringOrganizationId: number;
  originalSponsoringOrganizationId: number;
  isSupplierEnabled: boolean;
  suppliers: DropdownList[];
  supplierId: number;
  originalSupplierId: number;
  activityClosedDateTime: string;
  isFATypeEnabled: boolean;
  faTypes: FeedBackIdDescriptionParams[];
  faTypeId: number;
  originalFATypeId: number;
  isActivityDescriptionEnabled: boolean;
  activityDescriptions: FeedBackActivityDescriptionParams[];
  activityDescriptionId: number;
  originalActivityDescriptionId: number;
  isBusinessAreaEnabled: boolean;
  businessAreas: FeedBackBusinessAreaParams[];
  businessAreaId: number;
  originalBusinessAreaId: number;
  isTherapeuticAreaEnabled: boolean;
  therapeuticAreas: FeedBackTherapeuticAreaParams[];
  therapeuticAreaIds: string | null;
  originalTherapeuticAreaIds: string | null;
  isProductEnabled: boolean;
  products: FeedBackProductParams[];
  productIds: string | null;
  originalProductIds: string | null;
  isBrandEnabled: boolean;
  brands: FeedBackBrandParams[];
  allBrands: FeedBackBrandParams[];
  brandId: number;
  originalBrandId: number;
  isStartDateTimeEnabled: boolean;
  startDateTime: string | null;
  originalStartDateTime: string | null;
  isEndDateTimeEnabled: boolean;
  endDateTime: string | null;
  originalEndDateTime: string | null;
  timeZones: FeedBackTimeZoneParams[];
  brandActivityTimeZoneId: string;
  isTopicEnabled: boolean;
  topic: string;
  originalTopic: string;
  isWorkProductEnabled: boolean;
  workProducts: DropdownList[];
  workProductIds: string | null;
  originalWorkProductIds: string | null;
  activityNameInTLMap: string;
  originalActivityNameInTLMap: string;
  isParticipantEnabled: boolean;
  participants: DropdownWithStatusType[];
  participantIds: string | null;
  originalParticipantIds: string | null;
  isBusinessUnitEnabled: boolean;
  businessUnits: DropdownList[];
  businessUnitId: number;
  originalBusinessUnitId: number;
  sponsoringCountries: DropdownList[];
  sponsoringCountryId: number;
  originalSponsoringCountryId: number;
  isCVentMeetingEnabled: boolean;
  isCVentMeeting: boolean;
  cmsMeeting: string | null;
  originalCMSMeeting: string | null;
  isCVentMeetingIdVisible: boolean;
  isCVentMeetingIdEnabled: boolean;
  cVentMeetingId: string;
  originalCVentMeetingId: string;
  cVentMeetingIdToolTip: string;
  isMPCVisible: boolean;
  isMPCEnabled: boolean;
  mpcSuppliers: DropdownList[];
  mpcSupplierId: number;
  originalMPCSupplierId: number | null;
  isMPCEmailSectionVisible: boolean;
  mpcEmails: string;
  bnaApprovalConfirmation: number;
  isSponsorEmail1Enabled: boolean;
  sponsorContacts1: FeedBackSponsorContactParams[];
  sponsorEmail1: string | null;
  originalSponsorEmail1: string;
  sponsorContact1: string | null;
  sponsorContact1ProfileAndTrainingStatus: string | null;
  isSponsorEmail2Enabled: boolean;
  sponsorContacts2: FeedBackSponsorContactParams[];
  sponsorEmail2: string | null;
  originalSponsorEmail2: string;
  sponsorContact2: string | null;
  sponsorContact2ProfileAndTrainingStatus: string | null;
  isSponsorEmail3Enabled: boolean;
  sponsorContacts3: FeedBackSponsorContactParams[];
  sponsorEmail3: string | null;
  originalSponsorEmail3: string;
  sponsorContact3: string | null;
  sponsorContact3ProfileAndTrainingStatus: string | null;
  advisingCentralMembers: FeedBackAdvisingCentralMemberParams[];
  centralAdvisingEmailId: string;
  originalCentralAdvisingEmailId: string;
  centralAdvisingName: string | null;
  originalCentralAdvisingName: string;
  masterGrid: FeedBackMasterGridParams;
  isLillyActivityOwnerVisible: boolean;
  lillyActivityOwnerNameTitle: string | null;
  originalLillyActivityOwnerNameTitle: string;
  lillyActivityOwnerEmailId: string | null;
  tlMapActivityOwnerName: string;
  originalTLMapActivityOwnerName: string;
  isContactPersonEnabled: boolean;
  correspodenceName: string;
  originalCorrespodenceName: string;
  correspodenceTitle: string;
  originalCorrespodenceTitle: string;
  approvedDeniedBy: string;
  isBrandApproved: boolean | null;
  isBrandApprovedEnabled: boolean;
  approvedDeniedWhen: string;
  isPlannedEnabled: boolean;
  planHonorariumTotal: string | number | null;
  originalPlanHonorariumTotal: string | number | null;
  planExpenseTotal: string | number | null;
  originalPlanExpenseTotal: string | number | null;
  logisticsAmount: string | number | null;
  originalLogisticsAmount: string | number | null;
  eduAndPRIAmount: string | number | null;
  originalEDUAndPRIAmount: number | null;
  isBudgetApprovedEnabled: boolean;
  budgetStatus: number;
  originalBudgetStatus: number;
  actualEDUAndPRIAmount: number | null;
  actualTotalActivityBudget: number | null;
  approvalStatus: string | null;
  isAddCostCenterButtonEnabled: boolean;
  isActivityCostCentersEnabled: boolean;
  activityCostCenters: BrandActivityCostCentersParams[];
  deletedCostCenterList: string;
  isCostElementEnabled: boolean;
  costElement: string | null;
  originalCostElement: string | null;
  costDescriptions: DropdownList[];
  isCostDescriptionEnabled: boolean;
  costDescription: string | null;
  originalCostDescription: string | null;
  isPayOnSiteEnabled: boolean;
  paymentMode: boolean;
  payTo: string | null;
  originalPayTo: string | null;
  payOnSiteMailToCompany: string | null;
  originalPayOnSiteMailToCompany: string | null;
  payOnSiteContactNumber: string | null;
  originalPayOnSiteContactNumber: string | null;
  payOnSiteAddress1: string | null;
  originalPayOnSiteAddress1: string | null;
  payOnSiteAddress2: string | null;
  originalPayOnSiteAddress2: string | null;
  payOnSiteCity: string | null;
  originalPayOnSiteCity: string | null;
  payOnSiteStates: StatesProps[];
  payOnSiteStateId: number;
  originalPayOnSiteStateId: number | null;
  payOnSiteZip: string | null;
  payOnSiteCountries: DropdownList[];
  payOnSiteCountryId: number;
  originalPayOnSiteCountryId: number | null;
  userDetails: string | null;
  approverDetails: string | null;
  isBackButtonEnabled: boolean;
  isSubmitButtonEnabled: boolean;
  showSC1DropDown?: boolean;
  showSC2DropDown?: boolean;
  showSC3DropDown?: boolean;
  employeeId?: number;
  startTime?: string | null;
  endTime?: string | null;
  tlMapSuccessNote: string | null;
  mpcEmailArray?: string[];
}

export interface ARFParticipantsParams {
  rowNum: number;
  activityId: number;
  activityARFParticipantId: number;
  arfParticipantId: number;
  facultyKey: number;
  facultyName: string | null;
  lillyCustomerId: string | null;
  facultyType: string | null;
  roleId: number;
  participantLocation: string | null;
  isSupplementalTravel: number | null;
  activityDurationDays: number;
  activityDurationHours: number;
  activityDurationMinutes: number;
  participantPrepWorkHours: number;
  participantPrepWorkMinutes: number;
  checkType: string | null;
  msaContract: string | null;
  validationMessage: string | null;
  note: string | null;
  isReadyForProgram: boolean;
  facultyTypeId: number;
  tierLevel: number;
  trainingRequirement: string | null;
  ruleId: number | null;
  activityState: string | null;
  activityCity: string | null;
  activityRuleStartDate: string | null;
  complEndDate: string | null;
  activityRuleEndDate: string | null;
  ruleDescription: string | null;
  specialty1: string | null;
  paymentAmount: number;
  contractSigned: string | null;
  isNoteAdded: boolean;
  isDOL: boolean;
  honorarium: string | null;
  enterpriseId: string | null;
  referenceId: string | null;
  reasonForRevHonorarium: string | null;
  comments: string[] | null;
  revisedHonorarium: number | string | null;
}

export interface FeedBackParticipantsInformationParams {
  isSendAfterAllApprovals: boolean;
  isSentLater: boolean;
  sendLaterDate: string | null;
  actualSendLaterDate: string | null;
  dolFacultyDocumentInfos: DolFacultyDocumentInfosParams[];
  faculties: FieldActivityFacultyParams[];
  usBasedNonHCPFacultyInfos: FieldActivityFacultyParams[];
  usBasedPatientInfos: FieldActivityFacultyParams[];
  feedbackActivityOUSBasedHCPs: FeedbackActivityOUSBasedHCPParam[];
  feedbackActivityNonHCPs: FeedbackActivityNonHCPParams[];
  feedbackActivityNonMedicalInfos: NonMedicalPersonnelParams[];
  arfParticipants: ARFParticipantsParams[];
  nmpAreaIds: string | null;
  nmpInvitationReason: string | null;
  approverDetails: string | null;
  isNonMedicalPersonnel?: boolean | null;
  travelTypeForAll?: string | null;
  showNonMedicalPersonnelDependentFields?: boolean;
}

export interface DolFacultyDocumentInfosParams {
  id: number;
  facultyId: number;
  facultyName: string;
  fileName: string;
  systemGeneratedFileName: string;
  addedBy: string;
  addedWhen: string;
  status: boolean;
  isUpdated: boolean;
}

export interface FeedBackActivityLocationControlsPayloadParams {
  brandId: number;
  activitySupplierId: number;
  activityDescriptionId: number;
  travelValidationRequired: boolean;
  activityStatusId: number;
  activityAddedWhen: string | null;
}
export interface FeedbackActivityLocationControlParams {
  messageCode: number;
  message: string | null;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  isLocationNameEnabled: boolean;
  isLocationContactNameEnabled: boolean;
  isLocationContactNameRequired: boolean;
  isVirtualCheckboxVisible: boolean;
  isLocationAddress1Enabled: boolean;
  isLocationAddress2Enabled: boolean;
  isLocationCityEnabled: boolean;
  isLocationStateEnabled: boolean;
  states: StatesProps[];
  isLocationZipCodeEnabled: boolean;
  isLocationCountryEnabled: boolean;
  countries: CountriesProps[];
  isAccomodationSameEnabled: boolean;
  isAccomodationLocationNameEnabled: boolean;
  isAccomodationContactNameEnabled: boolean;
  isAccomodationAddress1Enabled: boolean;
  isAccomodationAddress2Enabled: boolean;
  isAccomodationCityEnabled: boolean;
  isAccomodationStateEnabled: boolean;
  accommodationStates: StatesProps[];
  isAccomodationZipCodeEnabled: boolean;
  isAccomodationCountryEnabled: boolean;
  isSubmitNextButtonEnabled: boolean;
  isTravelValidationRequired: boolean;
}

export interface AddARFParticipantPayloadParams {
  activityId: number;
  activityDescriptionId: number;
  brandId: number;
  startDateTime: string | null;
  endDateTime: string | null;
  activityAddedWhen: string | null;
  activitySupplierId: number;
  locationAddress1: string | null;
  locationAddress2: string | null;
  locationCity: string | null;
  locationStateId: number;
  locationCountryId: number;
  locationZipCode: string | null;
  isLocationGoogleVerified: boolean;
  brandActivityTimeZoneId: string;
  addedByProfileId: number;
  lillyActivityOwnerEmailId: string | null;
  cVentMeetingId: string;
  mpcSupplierId: number;
  travelValidationRequired: boolean;
  arfParticipant: ARFParticipantsParams;
}

export interface FeedbackProductsParams {
  id: number;
  name: string;
  brandCategoryId: number;
}
export interface FeedBackParticipantInfoControlsParams {
  isVisibleTravelRequiredFields: boolean;
  isEnabledTravelRequiredFields: boolean;
  invited: number;
  signedContract: number;
  registrationComplete: number;
  bookedTravel: number;
  isUSBasedHCPSectionVisible: boolean;
  isAddFacultyButtonEnabled: boolean;
  isSupplementalTravelColumnVisible: boolean;
  isSupplementalTravelColumnNonHCPPCGVisible: boolean;
  supplementalTravels: DropdownList[];
  travelTypes: DropdownList[];
  allTravelTypes: DropdownList[];
  participantTypes: DropdownList[];
  isPaymentLimitProductColumnVisible: boolean;
  products: FeedbackProductsParams[];
  states: StatesProps[];
  countries: CountriesProps[];
  isAddOUSBasedHCPsButtonEnabled: boolean;
  isOUSBasedHCPEditIconVisible: boolean;
  isUSBasedNonHCPSectionVisible: boolean;
  isUSBasedPCGSectionVisible: boolean;
  participantRoles: ParticipantRoleType[];
  activityDurationDaysList: DropdownList[];
  activityDurationHoursList: DropdownList[];
  activityDurationMinutesList: DropdownList[];
  prepTimeHoursList: DropdownList[];
  prepTimeMinutesList: DropdownList[];
  nonHCPLabel: string;
  isAddNonHCPsButtonEnabled: boolean;
  isNonHCPEditIconVisible: boolean;
  isNonMedicalWindowPeriod: boolean;
  isNonMedicalPersonnelEnabled: boolean;
  isNMPInvitationReasonEnabled: boolean;
  nonMedicalPersonnelPeriod: number;
  isAddNonMedicalPersonnelEnabled: boolean;
  nonMedicalPersonnelAreas: DropdownList[];
  isSubmitNextButtonEnabled: boolean;
  isLTSSupplier: boolean;
  isPWAdmin: boolean;
  isSuperUser: boolean;
  isMPCAssigned: boolean;
  honorariumVarianceInfoMessage: string;
  maxPerDayHours: number;
  paymentRequestLimit: number;
  receiptMaxFileSizeLimit: number;
  advisoryBoardsFacultyCount: number;
  facultyBlockedMessage: string | null;
  messageCode: number;
  message: string | null;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
}

export interface AddOUSBasedHCPInfoDataParams {
  feedbackActivityOUSBasedHCPs: FeedbackActivityOUSBasedHCPParam[];
  feedbackActivityOUSBasedHCPId: number;
  feedbackActivityAlertInfos: FeedbackActivityAlertInfosParams[];
  faTypeId: number;
  advisoryBoardsFacultyCount: number;
  facultyBlockedMessage: string | null;
  totalFacultyCount: number;
  selectedOUSBasedHCPs: FeedbackActivityOUSBasedHCPParam | null;
}

export interface NonMedicalPersonnelParams {
  nonMedicalPersonnelId: string | null;
  nmpFirstName: string | null;
  nmpLastName: string | null;
  nmpAreas: string | null;
  status: number;
  nmpName: string | null;
  email: string | null;
  id: number;
  supervisorName: string | null;
  approvalStatus: string | null;
  isActivityAttended: boolean;
  approvedWhen: string | null;
  nmpApprovedWhen: string | null;
}

export interface NonMedicalPersonnelSearchListParams {
  nonMedicalPersonnelId: string;
  nmpName: string;
  firstName: string;
  lastName: string;
  email: string;
  supervisorName: string;
}

export interface FeedbackActivityNonHCPParams {
  feedbackActivityNonHCPId: number;
  firstName: string | null;
  lastName: string | null;
  middleInitial: string | null;
  city: string | null;
  state: string | null;
  country: number;
  organizationName: string | null;
  individualOrOrganization: boolean | null;
  organizationRepresentative: boolean | null;
  consumerCapable: boolean | null;
  status: number;
  isActivityAttended: boolean;
  isUpdated: boolean;
  stateKey: number;
  suffix: string | null;
  facultyName: string | null;
  cityState: string | null;
  fileName: string | null;
  systemGeneratedFileName: string | null;
  file: string | null;
  fileExtension?: string | null;
}

export interface FeedbackActivityNonHCPPopupParams {
  feedbackActivityNonHCPs: FeedbackActivityNonHCPParams[];
  feedbackActivityNonHCPId: number;
  feedbackActivityAlertInfos: FeedbackActivityAlertInfosParams[];
  faTypeId: number;
  advisoryBoardsFacultyCount: number;
  facultyBlockedMessage: string | null;
  selectedNonHCP: FeedbackActivityNonHCPParams | null;
  totalFacultyCount: number;
}

export interface FeedbackActivityOUSBasedHCPParam {
  feedbackActivityOUSBasedHCPId: number;
  firstName: string | null;
  lastName: string | null;
  middleInitial: string | null;
  city: string | null;
  country: number;
  credentials: string | null;
  status: number;
  isActivityAttended: boolean;
  isUpdated: boolean;
  fCPADueDiligence: boolean;
  suffix: string | null;
  facultyName: string | null;
  fileName: string | null;
  systemGeneratedFileName: string | null;
  file: string | null;
  fileExtension?: string | null;
}

export interface FeedBackAdvisoryDescriptionParams {
  tlMaPBusinessNeed: string | null;
  originalDescriptionOfService: string | null;
  descriptionOfService: string | null;
  originalDescriptionforChair: string | null;
  descriptionforChair: string | null;
  originalDescriptionforPresenter: string | null;
  descriptionforPresenter: string | null;
  originalDescriptionforModerator: string | null;
  descriptionforModerator: string | null;
  approverDetails: string | null;
}

export interface FeedBackActivityAudienceParams {
  participantCount: number;
  attendeeInformations: ActivityAudienceAttendeeInformations[];
  audienceSpecialities: ActivityAudienceSpecialities;
  activityInvitees: ActivityAudienceActivityInvitees[];
  activityRSVPInfos: ActivityRSVPInfosListParams[];
  activityReceipts: ActivityReceiptsParams[];
  activitySISInfos: ActivitySISInfosListParams[];
}

interface ActivityReceiptsParams {
  id: number;
  activityId: number;
  fileName: string | null;
  receiptTypeId: number;
  receiptStatusId: number;
  comments: string | null;
  diplayName: string | null;
  status: boolean;
  addedBy: number;
  addedWhen: string;
  updatedBy: number | null;
  updatedWhen: string | null;
  amount: number | null;
  isGratuityPaid: boolean | null;
  gratuityAmount: number | null;
  isGratuityIncludedInAmount: boolean | null;
  totalAmount: number | null;
  thumbnailFileName: string | null;
  isAmountAttested: boolean;
  documentType: string | null;
  addedByName: string | null;
  receiptStatus: string | null;
}

export interface FeedBackAudienceSpecialitiesParams {
  specialtyIds: string | null;
  specialties: SpecialtiesParams[] | null;
}

export interface FeedBackMaterialOrderDetailsListParams {
  hasFacultyConfirmedJustNow: boolean;
  materialOrderDetails: MaterialOrderDetailsParams[];
}

export interface FeedBackIdDescriptionParams {
  id: number;
  description: string;
}

export interface FeedBackSponsorContactParams {
  id: string;
  name: string;
  profileAndTrainingStatus: string;
}

export interface FeedBackAdvisingCentralMemberParams {
  id: string;
  name: string;
  status: boolean;
}

export interface FeedBackBusinessAreaParams {
  id: number;
  name: string;
  status: boolean;
  isARF: boolean;
  activityDescriptionIds: string | null;
}

export interface FeedBackTherapeuticAreaParams {
  id: number;
  name: string;
  advisingCentralContactId: string;
}

export interface FeedBackProductParams {
  id: number;
  name: string;
  status: boolean;
  therapeuticAreaId: number;
}

export interface FeedBackBrandParams {
  id: number;
  name: string;
  status: boolean;
  supplierId: number;
  contractTypeId: number;
}

export interface FeedBackTimeZoneParams {
  id: string;
  name: string;
}

export interface FeedBackActivityDescriptionParams {
  id: number;
  name: string;
  activityTypeId: number;
  activitySubTypeId: number;
  status: boolean;
  ruleActivityTypeId: number;
  isTraining: boolean;
  brandIds: string | null;
  isARFActivityDescription: boolean;
  faTypeId: number;
  meetingTypeId: number;
  mappedCostElement: string | null;
  attendeeTypeIds: string | null;
  isARFParticipantRequired: boolean;
  isMedicalConsultingService: number;
  meetingId: string | null;
  isBNARequired: number;
  isConsultantRoleRequired: number;
  epri: boolean;
  isFacultyRecruitmentSpecialty: boolean;
  isVMSSpanishTranslation: boolean;
  defaultEndTimeHour: number;
  newAudiencePageEnabled: boolean;
  ignoreActivityDuration: boolean;
  isOUSGlobal: boolean;
  isOUS: boolean;
  isAdvisingActivity: boolean;
  isBrandPIDDistribution: boolean;
  activitySeries: boolean;
  proactiveBookingServices: boolean;
  advisingServiceDescription: boolean;
  isLMSDescription: number;
}

export interface FeedBackMasterGridParams {
  gridTitle: string;
  mainHeader1: string;
  cellHeader1: string;
  cellHeader2: string;
  gridDetails: FeedBackMasterGridDetailParams[];
  userDetails: string;
}

export interface FeedBackMasterGridDetailParams {
  cell1: string;
  cell2: string;
}

export interface fetchARFParticipantDetailsParams {
  arfParticipantId: number;
  activityARFParticipantId: number;
}
export interface FetchPNRRelatedActivities {
  activityId: number;
  facultyId: number;
  isPNRNumberVisible: boolean;
}

export interface AddLinkedRelatedActivitiesSearchParams {
  activityIdentity: string;
  brandId: number;
  startDate: string | null;
  endDate: string | null;
  activityDescriptionId: number;
  stateId: number;
}

export interface AddLinkedRelatedActivitiesSearchPayloadParams {
  activityId: number;
  activityIdentity: string | null;
  brandId: number;
  startDate: string;
  endDate: string;
  activityDescriptionId: number;
  stateId: number;
  facultyId: number;
  pageIndex: number;
  pageSize: number;
  profileId: number;
  supplierId: number;
}

export interface AddLinkedRelatedActivitiesList {
  rowId: number;
  originalAftId: number;
  facultyId: number;
  relatedActivityId: number;
  status: string;
  startDateTime: string;
  endDateTime: string;
  actualStartDate: string;
  preferredLocation: string;
  eventLocation: string;
  speaker: string;
  activityDescription: string;
  isTravelRequired: boolean;
  travelRequired: string;
  estimatedTravelDistance: number;
  isManualHonorariumTravelUpdate: boolean;
  travelFacultyId: number;
}

export interface PNRGenerateParams {
  activityId: number;
  selectedActivityIds: string;
  facultyId: number;
  activityMeetingInformationId: number;
  selectedActivityMeetingInformationIds: string;
  reason: string;
}
export interface PNRPossibleActivitiesParams {
  rowId: number;
  relatedActivityId: number;
  status: string;
  startDateTime: string;
  endDateTime: string;
  preferredLocation: string;
  eventLocation: string;
  speaker: string;
  activityDescription: string;
  travelRequired: string;
  meetingId: number | null;
}
export interface PNRRelatedActivitiesParams {
  rowId: number;
  activityId: number;
  facultyId: number;
  isPNRNumberVisible: boolean;
  activityIdentity: string;
  pnrNumber: string;
  activityMeetingInformationId: number;
  reason: string;
  activityType: string;
  isFromAHMFeed: boolean;
  isFeedBack: number;
}
export interface PNRLinkedActivitiesParams {
  rowId: number;
  relatedActivityId: number;
  facultyId: number;
  status: string;
  meetingId: number;
  startDateTime: string;
  endDateTime: string;
  preferredLocation: string;
  eventLocation: string;
  speaker: string;
  activityDescription: string;
  travelRequired: string;
}

export interface PNRRelatedActivities {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  isFeedBack: boolean;
  programType: string;
  isLTSSupplier: boolean;
  activityIdentity: string;
  faculty: string;
  eventLocation: string;
  possibleRelatedActivities: PaymentRequestRelatedActivitiesList[];
  linkedActivityList: PNRLinkedRelatedActivitiesParams[];
  additionalComments: string;
  currentPreferredAddress: string;
}

export interface PNRRelatedActivitiesSaveParams {
  activityId: number;
  facultyId: number;
  additionalComments: string;
  linkPNRActivities: string;
  pnr: string;
  meetingId: number;
  reason: string;
  removeLinkPNRActivities: string;
  profileId: number;
  switchedProfileId: number;
}
export interface PNRLinkedRelatedActivitiesParams {
  rowId: number;
  activityId: number;
  activityStatus: string;
  meetingId: number | null;
  startDateTime: string;
  endDateTime: string;
  preferredLocation: string;
  eventLocation: string;
  speaker: string;
  activityDescription: string;
  travelRequired: string | null;
}

export enum OrfFormFields {
  FacultyKey = "Faculty Key",
  LillyCustomerId = "Lilly Customer ID (LPID)",
  FacultyName = "Faculty Name",
  FacultyEmailAddress = "Faculty Email Address",
  ContractingYear = "Ownership/Contracting Year",
  ActivityType = "What type of activity will this faculty be utilized for?",
  ContractType = "Contract type",
  Brand = "What is the Brand or Disease State affiliated with this activity?",
  ActivityNumber = "What is the Activity Number?",
  ActivityStartDate = "Activity Start Date",
  SpecialLanguage = "Special Language",
  Division = "Division",
  Area = "Area",
  District = "District",
  SubmittedBy = "ORF Submitted By",
  EmailAddress = "Email Address",
  ContactNumber = "Contact Number",
  InitialRequester = "Initial Requester",
  RequesterEmail = "Requester Email",
  AdditionalRequesters = "Additional Requesters",
  AdditionalRequestersEmail = "Additional Requesters email",
  UrgentRequest = "Urgent ORF Request",
  Notes = "NOTES",
  EnterpriseId = "Enterprise ID",
  ReferenceId = "Reference ID",
}

export enum ActivityInfoSheet {
  FIELD = "Field",
  MEDICAL = "Medical",
  FEEDBACK = "Feedback",
  BRAND = "Brand",
  FIELDLTS = "FieldLTS",
  INFOSHEET = "Infosheet",
  INFOSHEETLTS = "InfosheetLTS",
}

export enum ActivityInfoSheetShortName {
  FIELD = "F",
  MEDICAL = "M",
  BRAND = "B",
}

export const SendCorrespondenceRadioList = [
  {name: "Attendee(s)", id: 1, enabled: true},
  {name: "Faculty", id: 2, enabled: true},
  {name: "Lead Representative", id: 3, enabled: true},
  {name: "Representative", id: 4, enabled: true},
  {name: "Both", id: 5, enabled: true},
  {name: "All", id: 6, enabled: true},
];

export const validateActivityIdentity = (value: any) => {
  const alphabetic = /^[a-zA-Z]+$/;
  if (isNaN(value)) {
    const shortName = value.substring(0, 3);
    const numberPart = value.substring(3);
    if (
      shortName.length < 3 ||
      !alphabetic.test(shortName) ||
      numberPart == null ||
      numberPart.length === 0 ||
      isNaN(numberPart)
    ) {
      return false;
    }
  }
  return true;
};

export const handlePasteOnlyNumbers = (
  event: React.ClipboardEvent<HTMLInputElement>,
) => {
  const pasteData = event.clipboardData.getData("Text");
  if (!/^\d*$/.test(pasteData)) {
    event.preventDefault();
  }
};

export const delayPromise = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const formatToDateTimeStringWithGivenTime = (ts: number) => {
  const d = new Date(ts);

  const pad = (n: any) => String(n).padStart(2, "0");

  // Build local string: YYYY-MM-DDTHH:mm:ss
  const local =
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

  return local;
};

export const removeSelectedKeysFromObject = (
  obj: any,
  keysToRemove: string[],
) => {
  const filteredObj = Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keysToRemove.includes(key)),
  );

  return filteredObj;
};
